import { Component, EventEmitter, HostBinding, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Sensor, SensorGroup, SensorReadingType } from '@models';
import { ChartDetails } from '@models/ChartFavorite/chartDetails.model';
import { ChartInfo } from '@models/Dashboard/chartData.model';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { ChartLabelInfo } from '@models/ChartLabel/chartLabelInfo.model';
import { ChartLabelRequest } from '@models/ChartLabel/chartLabelRequest.model';
import { ChartLabelProperties } from '@models/ChartLabel/chartLabelProperties.model';
import { AppMessageService } from '@services/message.service';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import { EChartsOption } from 'echarts';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { MinMaxScaleModalComponent, MinMaxScaleModalParams, MinMaxScaleResult } from './min-max-scale-modal/min-max-scale-modal.component';
import { ChartLabelEditorModalComponent } from './chart-label-editor-modal/chart-label-editor-modal.component';
import { SERIES_CHART_TYPES, SeriesChartType, SeriesTypeModalComponent, SeriesTypeModalParams, SeriesTypeResult } from './series-type-modal/series-type-modal.component';
import { SensorReadingsService } from '../../sensor-readings/sensor-readings.service';
import { labelFontScale } from 'app/shared/classes/labelStyling';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

// The type of ECharts series to render for the readings.
export type EChartType = 'line' | 'bar' | 'scatter' | 'candlestick' | 'polar';

// Aggregation period used when rendering a candlestick chart.
export type CandlestickBucket = 'hour' | 'day' | 'week';

// The control groups above the chart that collapse to a label + chevron when
// they hold no active values, to keep the control row compact.
export type ControlGroup = 'outliers' | 'aggregates' | 'daily' | 'colors';

// How the cross-sensor aggregate series are partitioned: one line per reading
// type, or one line per sensor group per reading type.
export type AggregateGroupMode = 'readingType' | 'groupAndReadingType';

// How the daily series are partitioned: one line per reading type (all sensors
// pooled), one per sensor group per reading type, or one per individual sensor
// per reading type.
export type DailyGroupMode = 'readingType' | 'groupAndReadingType' | 'readingTypeAndSensor';

// One sensor's plotted points for a single reading type, tagged with the sensor's
// uuid so the aggregate builder can partition the series by sensor group.
interface PerSensorSeries {
  uuid: string;
  points: { name: any; value: any }[];
}

@Component({
  standalone: false,
  selector: 'chart-readings-echart-modal',
  templateUrl: './chart-readings-echart-modal.component.html',
  styleUrls: ['./chart-readings-echart-modal.component.scss']
})

export class ChartReadingsEchartModalComponent implements OnInit, OnChanges, OnDestroy {

  @Input() sensors: Sensor[];
  @Input() sensorReadings: SensorReadingInfo[];
  @Input() sensorReadingTypes: SensorReadingType[];
  @Input() includeOrigValues: boolean;
  @Input() includeCalValues: boolean;

  // When true the chart renders on a very dark gray background instead of white.
  // Owned by the host component (which also persists the preference).
  @Input() darkBackground: boolean = false;

  // CSS height for each chart. Hosts should pass a value that fits their layout
  // (e.g. a fixed pixel height inside a modal). Defaults to a viewport-relative
  // height for full-page hosts. Ignored when fillHeight is true.
  @Input() chartHeight: string = 'calc(92vh - 260px)';

  // When true, the component fills its host's height: the controls take their
  // natural (possibly wrapping) height and the chart flexes to fill the rest.
  // Use this inside a bounded container (e.g. a modal) so a growing control row
  // does not push the chart out of view.
  @Input() fillHeight: boolean = false;

  @HostBinding('class.fill-height') get fillHeightClass(): boolean {
    return this.fillHeight;
  }

  // Optional extra series (each { name, series: [{ name: Date, value }] }) to
  // overlay on top of the reading series — e.g. event-action markers. Appended
  // to every chart as-is.
  @Input() overlaySeries: { name: string; series: { name: any; value: any }[] }[] = [];

  // Whether to show the chart legend (and its Select All / Unselect All buttons).
  @Input() showLegend: boolean = true;

  // Minimal mode: render just the chart (series + axes) with no chrome — hides
  // the control row, the legend, the x/y dataZoom sliders, and the toolbox. Used
  // for compact embeds such as the dashboard chart-favorite cards.
  @Input() minimal: boolean = false;

  // Exposed as a host class so the SCSS can give compact embeds (dashboard tiles) a
  // lower .echart min-height than full-page charts, letting short tiles honor their
  // [chartHeight] instead of being floored.
  @HostBinding('class.minimal') get minimalClass(): boolean {
    return this.minimal;
  }

  // Whether to show a vertical slider on the left that scales the (left) y-axis
  // min/max — the ECharts equivalent of the old y-axis range slider.
  @Input() showYAxisSlider: boolean = false;

  // Per-Y-axis manual scaling, keyed by axis (a reading type name, 'Value' for
  // the single primary axis, or 'Sum'). Set by clicking a Y-axis's values (see
  // onChartClick -> openManualScaleModal). A bound of null autoscales that end;
  // an axis with any bound set shows a '*' after its title. Persisted in
  // ChartDetails (so it rides both localStorage and saved favorites).
  manualScaleByAxis: { [axisKey: string]: { min: number | null; max: number | null } } = {};

  // The ordered base axis keys used to build each chart's yAxis array, so a click
  // event's componentIndex can be mapped back to the axis it belongs to.
  private axisKeysByChart: string[][] = [];

  // Saved chart options to re-apply on load (from a Chart Favorite's ChartDetails
  // JSON). Applied to this component's own controls right after it (re)builds its
  // chartInfos, so a restored favorite renders with the user's saved options.
  @Input() savedChartDetails: ChartDetails = null;

  // Emitted whenever the user changes a chart option here (chart type, aggregate
  // toggles, colors, buckets, axes, ignore-outlier bounds, …). Lets the host
  // re-persist its "last used" localStorage snapshot so these survive a reload.
  @Output() chartDetailsChanged = new EventEmitter<ChartDetails>();

  // The chart favorite these labels belong to. When > 0 the component fetches and
  // renders that favorite's labels (annotations). 0/undefined => no labels. Other
  // hosts (dashboard tiles, etc.) simply leave it unset.
  @Input() chartFavoriteId: number = 0;

  // When true, the "Labels" button is shown (standard combined time charts only)
  // so the user can add/edit/drag labels. Gated by the host on a saved favorite +
  // the EditChart permission.
  @Input() labelsEditable: boolean = false;

  // The favorite's labels (fetched when chartFavoriteId changes). Rendered as a
  // dedicated annotation series; edited in-place via drag handles + the editor.
  labels: ChartLabelInfo[] = [];

  // True while the user is editing labels (drag handles shown, editor modal open).
  labelsEditMode: boolean = false;

  // Selected sensor groups, used by the "TimeSincePlanting" line x-axis mode:
  // each group's plantDate is day 0 for the sensors that belong to that group.
  @Input() sensorGroups: SensorGroup[] = [];

  // Fallback day-0 for TimeSincePlanting when a sensor's group has no plantDate
  // (or the sensor is not in any provided group) — typically the selected date
  // range's start date.
  @Input() dateRangeStart: Date;

  // New input to select which type of ECharts chart to render.
  @Input() echartType: EChartType = 'line';

  // Available chart types shown in the selector.
  echartTypes: { value: EChartType; label: string }[] = [
    { value: 'line', label: 'Line' },
    { value: 'bar', label: 'Bar' },
    { value: 'scatter', label: 'Scatter' },
    { value: 'candlestick', label: 'Candlestick' },
    { value: 'polar', label: 'Polar' }
  ];

  // Angular bucket size (degrees) for the wind-rose polar chart.
  polarBucketDegrees: number = 5;
  polarBuckets: { value: number; label: string }[] = [
    { value: 5, label: '5°' },
    { value: 10, label: '10°' },
    { value: 22.5, label: '22.5° (16-pt)' },
    { value: 45, label: '45° (8-pt)' }
  ];

  // When enabled on a combined chart with more than one reading type, each
  // reading type gets its own independent Y-axis (first on the left, the rest
  // stacked on the right), each autoscaled to fill the available space.
  overlayIndependentYAxis: boolean = false;

  // Which reading type's independent Y-axis is shown on the left (the rest stack
  // on the right). Set by clicking an axis title; when unset or no longer present
  // we fall back to the first reading type. Persisted via ChartDetails.
  leftAxisReadingType: string | undefined;

  // Scatter correlation: selected X and Y data series (by series name). The X
  // list may include the special value 'Time' to plot against the time axis.
  // Only used when echartType === 'scatter' on a combined chart.
  static readonly SCATTER_TIME = 'Time';
  scatterXSeries: string[] = [ ChartReadingsEchartModalComponent.SCATTER_TIME ];
  scatterYSeries: string[] = [];

  // Line-chart x-axis mode:
  //  'time'              - normal time x-axis (default)
  //  'minOfDay'          - split each series by calendar day and overlay on a
  //                        minute/time-of-day (0-1440) x-axis, one line per day.
  //  'TimeSincePlanting' - x = whole days since each sensor group's plantDate
  //                        (day 0); one continuous line per series per group.
  lineXMode: 'time' | 'minOfDay' | 'timeSincePlanting' = 'time';

  lineXModes: { value: 'time' | 'minOfDay' | 'timeSincePlanting'; label: string }[] = [
    { value: 'time', label: 'Time' },
    { value: 'minOfDay', label: 'MinOfDay' },
    { value: 'timeSincePlanting', label: 'timeSincePlanting' }
  ];

  // How the cross-sensor aggregate series (Average / Median / Trimmed-Mean) are
  // partitioned:
  //  'readingType'          - one aggregate line per reading type, over every
  //                           selected sensor (default, the original behavior).
  //  'groupAndReadingType'  - one aggregate line per sensor group per reading
  //                           type, so groups can be compared against each other.
  // Only offered when more than one sensor group is selected (see
  // showAggregateGroupPicker).
  aggregateGroupMode: AggregateGroupMode = 'readingType';

  aggregateGroupModes: { value: AggregateGroupMode; label: string }[] = [
    { value: 'readingType', label: 'by Reading Type' },
    { value: 'groupAndReadingType', label: 'by Group & Reading Type' }
  ];

  // How the daily series are partitioned across sensors (independent of the
  // aggregate grouping): one line per reading type (all sensors pooled), one per
  // sensor group per reading type, or one per individual sensor per reading type.
  dailyGroupMode: DailyGroupMode = 'readingType';

  dailyGroupModes: { value: DailyGroupMode; label: string }[] = [
    { value: 'readingType', label: 'by Reading Type' },
    { value: 'groupAndReadingType', label: 'by Group & Reading Type' },
    { value: 'readingTypeAndSensor', label: 'by Reading Type and Sensor' }
  ];

  // Bucket label used for sensors that are in none of the selected sensor groups,
  // so they still contribute to an aggregate instead of being dropped.
  private static readonly UNGROUPED_LABEL = 'Ungrouped';

  // Aggregation period for the candlestick chart (only used when echartType === 'candlestick').
  candlestickBucket: CandlestickBucket = 'day';

  // Available aggregation periods shown when candlestick is selected.
  candlestickBuckets: { value: CandlestickBucket; label: string }[] = [
    { value: 'hour', label: 'By Hour' },
    { value: 'day', label: 'By Day' },
    { value: 'week', label: 'By Week' }
  ];

  modalDragOn: boolean = false;
  exportOptionsModalRef: BsModalRef;
  username = 'unknown';

  // Background / axis / text colors used when rendering the chart.
  private readonly lightBackgroundColor = '#ffffff';
  private readonly darkBackgroundColor = '#222222';

  // Horizontal space (px) reserved on the right of the grid for the vertical legend.
  private readonly legendWidth = 220;

  // Gap (px) kept between the (right) y-axis and the legend so axis value labels
  // are not covered by the legend.
  private readonly legendGap = 50;

  // Horizontal space (px) reserved on the left for the vertical y-axis slider.
  private readonly yAxisSliderWidth = 40;

  // Default 5-color palette (reused from the ngx-charts version). Series cycle
  // through the active palette by index, wrapping around when there are more
  // series than colors.
  private static readonly DEFAULT_COLOR_SCHEME: string[] = [ '#046dd6', '#800080', '#42f55a', '#ffc107', '#fa0f0f' ];

  // localStorage key for persisting the (possibly customized) color palette.
  private static readonly COLOR_SCHEME_STORAGE_KEY = 'EchartColorScheme';

  // Active color palette. Editable via the color pickers; persisted locally.
  colorScheme: string[] = [ ...ChartReadingsEchartModalComponent.DEFAULT_COLOR_SCHEME ];

  chartInfos: ChartInfo[] = [];

  // The savedChartDetails object last applied, so a given favorite's options are
  // applied only once (on load) and do not clobber the user's later manual
  // changes when the chart set is rebuilt (e.g. readings reload).
  private appliedChartDetailsRef: ChartDetails = null;

  // One ECharts option object per ChartInfo (parallel array).
  chartOptions: EChartsOption[] = [];

  // ECharts instances per chart index, captured on (chartInit), used to drive
  // the Select All / Unselect All legend actions.
  private echartInstances: any[] = [];

  // Name/id of the dedicated, invisible series that carries all label markers
  // (markLine/markArea/markPoint). Kept out of the legend and legend-width/CSV.
  private static readonly ANNOTATION_SERIES_NAME = '__annotations__';

  // The favorite id whose labels we last fetched, so repeated input changes don't
  // refetch (and so switching favorites does).
  private lastFetchedFavoriteId: number = -1;

  // The open label-editor modal ref (null when not editing).
  private labelEditorRef: BsModalRef = null;

  // Label fonts (and padding / default offsets) scale with the chart's width the same way image favorite
  // labels scale with the image: authored for a 600px-wide chart, limited to 0.5x - 2x. Kept current by
  // a ResizeObserver on the chart's container (see watchChartSize); 1 until the first measurement.
  private labelScale = 1;
  private chartSizeObserver: ResizeObserver | null = null;
  private chartSizeFrame = 0;

  // Legend selection state per chart index ({ seriesName: isSelected }), so the
  // user's show/hide choices survive an option rebuild (chart type, aggregation,
  // outliers, etc.). null/undefined means "no saved state" (all shown).
  private legendSelected: ({ [name: string]: boolean } | null)[] = [];

  // Distinguishes this chart's element ids from those of other charts on the same page
  // (the Analyze page can show several chart tiles at once).
  private static nextInstanceId = 0;
  readonly instanceId = ChartReadingsEchartModalComponent.nextInstanceId++;

  // Per-series chart type overrides ({ seriesName: type }); a series not listed
  // follows echartType. Persisted on the favorite as ChartDetails.seriesTypes.
  seriesTypeOverrides: { [name: string]: SeriesChartType } = {};

  // Types offered in the right-click menu (same list as the per-series dialog).
  readonly seriesChartTypes = SERIES_CHART_TYPES;

  // The open right-click menu (null when closed): the series it applies to and
  // its position within the chart container.
  seriesMenu: { name: string; x: number; y: number } | null = null;

  // Current zoom (dataZoom) range per chart index, so it can be re-applied when
  // the options are rebuilt (e.g. changing chart type, outliers, aggregates).
  // Values are percentages (0-100); null means "no saved zoom" (show all).
  private zoomRanges: ({ start: number; end: number } | null)[] = [];

  // Current y-axis slider range per chart index (percentages 0-100), retained
  // across option rebuilds like the x-axis zoom. Only used when showYAxisSlider.
  private yZoomRanges: ({ start: number; end: number } | null)[] = [];

  // Which of the collapsible control groups (Ignore Outliers / Aggregate Series /
  // Colors) are expanded, per chart index. Seeded from "does this group have
  // active values" on every rebuild, so a restored favorite reveals its settings.
  private expandedGroups: Set<ControlGroup>[] = [];

  // Per chart index, the groups the user has explicitly clicked. Those keep the
  // state the user chose instead of being re-seeded on the next rebuild, so a
  // group never pops open (or shut) while the user is working in it.
  private pinnedGroups: Set<ControlGroup>[] = [];


  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              public _sensorService: SensorService, public _userService: UserService, public _appMessageService: AppMessageService,
              private _sensorReadingsService: SensorReadingsService, private zone: NgZone) {
  }


  ngOnDestroy(): void {
    this.chartSizeObserver?.disconnect();
    cancelAnimationFrame(this.chartSizeFrame);
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.loadColorScheme();
    this.initChartInfo();
  }


  // Restore the saved color palette, falling back to the default 5 colors.
  private loadColorScheme() {
    const saved = localStorage.getItem(ChartReadingsEchartModalComponent.COLOR_SCHEME_STORAGE_KEY);
    if (saved != undefined && saved !== '') {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(c => typeof c === 'string')) {
          this.colorScheme = parsed;
          return;
        }
      } catch {
        // Ignore malformed saved value and use the default palette.
      }
    }
    this.colorScheme = [ ...ChartReadingsEchartModalComponent.DEFAULT_COLOR_SCHEME ];
  }


  // Persist the current palette.
  private saveColorScheme() {
    localStorage.setItem(ChartReadingsEchartModalComponent.COLOR_SCHEME_STORAGE_KEY, JSON.stringify(this.colorScheme));
  }


  // Whether the scatter X/Y correlation dropdowns should be shown.
  get showScatterAxisPickers(): boolean {
    return this.echartType === 'scatter';
  }

  // Scatter with an explicit non-Time X series (value vs value), which replaces
  // the time chart with a correlation plot.
  private get isScatterCorrelationActive(): boolean {
    return this.echartType === 'scatter'
      && this.scatterYSeries.length > 0
      && !(this.scatterXSeries.length === 1 && this.scatterXSeries[0] === ChartReadingsEchartModalComponent.SCATTER_TIME);
  }

  // Per-series chart types only apply to the standard time chart: candlestick,
  // polar, scatter correlation, Min-of-Day and Time-Since-Planting build their
  // own series. Overrides are kept (and saved) while one of those modes is on.
  get seriesTypeOverridesApplicable(): boolean {
    return !this.minimal
      && (this.echartType === 'line' || this.echartType === 'bar' || this.echartType === 'scatter')
      && !this.isMinOfDayActive
      && !this.isTimeSincePlantingActive
      && !this.isScatterCorrelationActive;
  }

  get seriesTypeOverrideCount(): number {
    return Object.keys(this.seriesTypeOverrides).length;
  }

  // The chart-level type as a per-series type (the default for un-overridden series).
  private get defaultSeriesType(): SeriesChartType {
    return this.echartType === 'bar' || this.echartType === 'scatter' ? this.echartType : 'line';
  }

  // type / symbol / fill settings for a data series, honoring its override.
  private seriesStyle(name: string): { type: 'line' | 'bar' | 'scatter'; showSymbol: boolean; symbolSize: number; areaStyle?: { opacity: number } } {
    const effective = this.seriesTypeOverrides[name] ?? this.defaultSeriesType;
    switch (effective) {
      case 'area':
        return { type: 'line', showSymbol: false, symbolSize: 4, areaStyle: { opacity: 0.25 } };
      case 'scatter':
        return { type: 'scatter', showSymbol: true, symbolSize: 6 };
      case 'bar':
        return { type: 'bar', showSymbol: false, symbolSize: 4 };
      default:
        return { type: 'line', showSymbol: false, symbolSize: 4 };
    }
  }

  // Stable arrays for the scatter X/Y dropdowns (recomputed only when the built
  // series change, so ng-select gets a stable [items] reference).
  availableSeriesNames: string[] = [];
  scatterXOptions: string[] = [ ChartReadingsEchartModalComponent.SCATTER_TIME ];


  // Refresh the scatter dropdown option lists from the currently built series.
  private refreshScatterOptions(): void {
    const chart = this.chartInfos[0];
    this.availableSeriesNames = chart != undefined ? chart.chartData.map(d => d.name) : [];
    this.scatterXOptions = [ ChartReadingsEchartModalComponent.SCATTER_TIME, ...this.availableSeriesNames ];
  }


  // Rebuild when the scatter X/Y series selections change.
  onScatterSeriesChange(): void {
    this.buildEchartOptions();
    this.emitChartDetailsChanged();
  }


  // Whether the loaded readings span more than one calendar day (local time).
  get readingsSpanMultipleDays(): boolean {
    if (this.sensorReadings == undefined || this.sensorReadings.length === 0) {
      return false;
    }
    const days = new Set<string>();
    for (const r of this.sensorReadings) {
      const d = r.createdDate instanceof Date ? r.createdDate : new Date(r.createdDate);
      days.add(moment(d).format('YYYY-MM-DD'));
      if (days.size > 1) {
        return true;
      }
    }
    return false;
  }


  // Whether to show the line-chart x-axis mode dropdown.
  get showLineXModePicker(): boolean {
    return this.echartType === 'line' && this.readingsSpanMultipleDays;
  }


  // The x-axis modes offered in the dropdown. timeSincePlanting is only offered
  // when sensor groups are provided (its day-0 comes from group plantDates).
  get availableLineXModes(): { value: 'time' | 'minOfDay' | 'timeSincePlanting'; label: string }[] {
    const hasGroups = this.sensorGroups != undefined && this.sensorGroups.length > 0;
    return hasGroups ? this.lineXModes : this.lineXModes.filter(m => m.value !== 'timeSincePlanting');
  }


  // Whether the Line + MinOfDay mode is currently active.
  get isMinOfDayActive(): boolean {
    return this.echartType === 'line' && this.lineXMode === 'minOfDay' && this.readingsSpanMultipleDays;
  }


  // Whether the Line + timeSincePlanting mode is currently active.
  get isTimeSincePlantingActive(): boolean {
    return this.echartType === 'line' && this.lineXMode === 'timeSincePlanting'
      && this.sensorGroups != undefined && this.sensorGroups.length > 0;
  }


  // Whether to show the aggregate-series grouping dropdown. It only means
  // something when there is more than one sensor group to compare, and not in
  // Min of Day mode (that aggregates a series across days, not across sensors).
  get showAggregateGroupPicker(): boolean {
    return this.sensorGroups != undefined && this.sensorGroups.length > 1 && !this.isMinOfDayActive;
  }


  // Whether aggregates should actually be partitioned per sensor group. Guarded by
  // showAggregateGroupPicker so a restored favorite saved with the group mode
  // falls back to the single all-sensor aggregate when the current selection no
  // longer has multiple groups.
  private get groupAggregates(): boolean {
    return this.aggregateGroupMode === 'groupAndReadingType' && this.showAggregateGroupPicker;
  }


  // Whether the daily series should be partitioned per sensor group (same guard
  // as groupAggregates but driven by the independent dailyGroupMode).
  private get groupDaily(): boolean {
    return this.dailyGroupMode === 'groupAndReadingType' && this.showAggregateGroupPicker;
  }


  // Options shown in the Daily Series grouping dropdown. 'by Group & Reading
  // Type' only makes sense with more than one sensor group, so it is listed only
  // then — or when it is the current selection (so a restored favorite doesn't
  // leave the dropdown blank).
  get dailyGroupModeOptions(): { value: DailyGroupMode; label: string }[] {
    return this.dailyGroupModes.filter(m =>
      m.value !== 'groupAndReadingType' || this.showAggregateGroupPicker || this.dailyGroupMode === 'groupAndReadingType');
  }


  // Daily Series is only meaningful on the standard time-based chart. A non-time
  // lineXMode (Min-of-Day, Time-Since-Planting) and candlestick/polar/scatter
  // re-bucket or reinterpret the x-axis and would mangle day-based series, so the
  // control is hidden and not computed there.
  get dailySeriesApplicable(): boolean {
    return (this.echartType === 'line' || this.echartType === 'bar') && this.lineXMode === 'time';
  }


  // Rebuild when the line x-axis mode changes.
  onLineXModeChange(): void {
    // Rebuild data (not just re-render) so Daily Series are added/removed to
    // match the new mode's applicability (they only apply to the standard time
    // chart and would otherwise linger, mangled, in Min-of-Day etc.).
    this.updateChartData();
    this.emitChartDetailsChanged();
  }


  // Whether to show the polar angular-bucket-size dropdown. Only relevant to the
  // wind-rose variant, i.e. when at least one series on the chart is directional.
  get showPolarBucketPicker(): boolean {
    if (this.echartType !== 'polar') {
      return false;
    }
    return this.chartInfos.some(chart =>
      chart.chartData.some(d => !d.isAggregate && this.isDirectionalSeries(d.name, d.series)));
  }


  // Rebuild when the polar bucket size changes.
  onPolarBucketChange(): void {
    this.buildEchartOptions();
    this.emitChartDetailsChanged();
  }


  // A series is "directional" (wind-rose candidate) when its reading type name
  // suggests a direction, or all its values fall within 0-360.
  private isDirectionalSeries(name: string, series: { name: any; value: any }[]): boolean {
    const type = this.readingTypeOfSeries(name).toLowerCase();
    if (type.includes('direction') || type.includes('wind') || type.includes(' dir') || type.endsWith('dir') || type === 'dir') {
      return true;
    }
    if (series == undefined || series.length === 0) {
      return false;
    }
    // Otherwise treat as directional only if every value is a valid 0-360 number.
    return series.every(p => {
      const v = Number(p.value);
      return !isNaN(v) && v >= 0 && v <= 360;
    });
  }


  // trackBy for the palette swatches so editing a color does not recreate the
  // other <input> elements (which would close an open color picker mid-edit).
  trackByIndex(index: number): number {
    return index;
  }


  // Change one palette color, then persist and re-render.
  onPaletteColorChange(index: number, color: string) {
    if (index < 0 || index >= this.colorScheme.length) {
      return;
    }
    this.colorScheme[index] = color;
    this.colorScheme = [ ...this.colorScheme ];
    this.saveColorScheme();
    this.buildEchartOptions();
    this.emitChartDetailsChanged();
  }


  // Add a new color to the palette, then persist and re-render.
  addPaletteColor() {
    this.colorScheme = [ ...this.colorScheme, '#000000' ];
    this.saveColorScheme();
    this.buildEchartOptions();
    this.emitChartDetailsChanged();
  }


  // Reset the palette back to the default 5 colors, then persist and re-render.
  resetColorScheme() {
    this.colorScheme = [ ...ChartReadingsEchartModalComponent.DEFAULT_COLOR_SCHEME ];
    this.saveColorScheme();
    this.buildEchartOptions();
    this.emitChartDetailsChanged();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnChanges(changes: SimpleChanges) {
    // A favorite change (re)loads that favorite's labels. Done independently of
    // the chart rebuild below since labels fetch asynchronously and are read on
    // every option build.
    if (changes['chartFavoriteId'] != undefined) {
      this.loadChartLabels();
    }

    // A dark-background-only change just needs a re-style, not a full rebuild
    // (which would reset the zoom). A favorite-id / labelsEditable-only change
    // likewise doesn't rebuild the chart set. Everything else rebuilds it.
    const keys = Object.keys(changes);
    const nonRebuildKeys = new Set(['darkBackground', 'chartFavoriteId', 'labelsEditable']);
    const needsRebuild = keys.some(k => !nonRebuildKeys.has(k));

    if (!needsRebuild) {
      if (keys.includes('darkBackground') && !changes['darkBackground'].firstChange) {
        this.buildEchartOptions();
      }
      return;
    }

    this.initChartInfo();
  }


  initChartInfo() {
    // The chart set is being rebuilt, so any retained zoom no longer applies. The
    // legend selection is kept: it is keyed by series name and there is a single
    // chart, so it stays valid (and a restored favorite's hidden series must
    // survive the readings reload that follows loading it).
    this.zoomRanges = [];
    this.yZoomRanges = [];

    // Preserve the user's per-chart selections (aggregate toggles + ignore-outlier
    // bounds) across the rebuild, keyed by position. Without this a readings
    // reload (or any input change) silently clears them — and they would be lost
    // from a saved favorite / localStorage snapshot too.
    const prior = this.chartInfos.map(c => ({
      yMinIgnore: c.yMinIgnore,
      yMaxIgnore: c.yMaxIgnore,
      showAverage: c.showAverage,
      showMedian: c.showMedian,
      showTrimmedMean: c.showTrimmedMean,
      showSum: c.showSum,
      showDailyAverage: c.showDailyAverage,
      showDailyMedian: c.showDailyMedian,
      showDailyTrimmedMean: c.showDailyTrimmedMean,
      showDailyMin: c.showDailyMin,
      showDailyMax: c.showDailyMax,
      showDailySum: c.showDailySum
    }));

    // A single chart holds all the selected reading types.
    this.chartInfos = [{
      sensor: undefined,
      sensorName: 'Chart with all selected readings types',
      sensorReadings: [],
      dataTypes: this.sensorReadingTypes,
      chartData: [],
      chartDays: 0,
      yMinScale: null,
      yMaxScale: null,
      yMinIgnore: '',
      yMaxIgnore: '',
      yMinData: null,
      yMaxData: null,
      view: [1200, 620]
    }];

    // Re-apply the preserved per-chart selections to the matching new charts.
    this.chartInfos.forEach((chart, i) => {
      const previous = prior[i];
      if (previous != undefined) {
        chart.yMinIgnore = previous.yMinIgnore;
        chart.yMaxIgnore = previous.yMaxIgnore;
        chart.showAverage = previous.showAverage;
        chart.showMedian = previous.showMedian;
        chart.showTrimmedMean = previous.showTrimmedMean;
        chart.showSum = previous.showSum;
        chart.showDailyAverage = previous.showDailyAverage;
        chart.showDailyMedian = previous.showDailyMedian;
        chart.showDailyTrimmedMean = previous.showDailyTrimmedMean;
        chart.showDailyMin = previous.showDailyMin;
        chart.showDailyMax = previous.showDailyMax;
        chart.showDailySum = previous.showDailySum;
      }
    });

    this.applySavedChartDetails();

    // After the favorite / preserved values are in place, so a group with active
    // values starts expanded.
    this.seedExpandedGroups();

    this.updateChartData();
  }


  // Apply a saved ChartDetails bag (from a restored Chart Favorite) to this
  // component's controls and per-chart settings. Runs once per distinct
  // savedChartDetails object so it does not overwrite the user's later manual
  // changes when the chart set is rebuilt (e.g. on a readings reload).
  private applySavedChartDetails(): void {
    const details = this.savedChartDetails;
    if (details == undefined || details === this.appliedChartDetailsRef) {
      return;
    }
    this.appliedChartDetailsRef = details;

    if (details.echartType != undefined) { this.echartType = details.echartType as EChartType; }
    if (details.lineXMode != undefined) { this.lineXMode = details.lineXMode as any; }
    if (details.scatterXSeries != undefined) { this.scatterXSeries = [ ...details.scatterXSeries ]; }
    if (details.scatterYSeries != undefined) { this.scatterYSeries = [ ...details.scatterYSeries ]; }
    if (details.overlayIndependentYAxis != undefined) { this.overlayIndependentYAxis = details.overlayIndependentYAxis; }
    if (details.leftAxisReadingType != undefined) { this.leftAxisReadingType = details.leftAxisReadingType; }
    if (details.manualScaleByAxis != undefined) { this.manualScaleByAxis = this.cloneScaleMap(details.manualScaleByAxis); }
    if (details.candlestickBucket != undefined) { this.candlestickBucket = details.candlestickBucket as CandlestickBucket; }
    if (details.polarBucketDegrees != undefined) { this.polarBucketDegrees = details.polarBucketDegrees; }
    if (Array.isArray(details.colorScheme) && details.colorScheme.length > 0) { this.colorScheme = [ ...details.colorScheme ]; }

    // Replace (not merge) the legend selection, so a favorite without hiddenSeries
    // shows every series rather than inheriting the previous favorite's hidden set.
    const hidden: { [name: string]: boolean } = {};
    if (Array.isArray(details.hiddenSeries)) {
      details.hiddenSeries.forEach(name => hidden[name] = false);
    }
    this.legendSelected = Object.keys(hidden).length > 0 ? [ hidden ] : [];

    // Same for the per-series chart types: replace, keeping only valid types.
    const validTypes = new Set<string>(SERIES_CHART_TYPES.map(t => t.value));
    const overrides: { [name: string]: SeriesChartType } = {};
    if (details.seriesTypes != undefined) {
      Object.keys(details.seriesTypes).forEach(name => {
        if (validTypes.has(details.seriesTypes[name])) {
          overrides[name] = details.seriesTypes[name] as SeriesChartType;
        }
      });
    }
    this.seriesTypeOverrides = overrides;
    if (details.aggregateGroupMode != undefined) { this.aggregateGroupMode = details.aggregateGroupMode as AggregateGroupMode; }
    if (details.dailyGroupMode != undefined) { this.dailyGroupMode = details.dailyGroupMode as DailyGroupMode; }

    // Per-chart settings (there is a single chart; still applied to all).
    this.chartInfos.forEach(chart => {
      if (details.yMinIgnore != undefined) { chart.yMinIgnore = details.yMinIgnore; }
      if (details.yMaxIgnore != undefined) { chart.yMaxIgnore = details.yMaxIgnore; }
      if (details.showAverage != undefined) { chart.showAverage = details.showAverage; }
      if (details.showMedian != undefined) { chart.showMedian = details.showMedian; }
      if (details.showTrimmedMean != undefined) { chart.showTrimmedMean = details.showTrimmedMean; }
      if (details.showSum != undefined) { chart.showSum = details.showSum; }
      if (details.showDailyAverage != undefined) { chart.showDailyAverage = details.showDailyAverage; }
      if (details.showDailyMedian != undefined) { chart.showDailyMedian = details.showDailyMedian; }
      if (details.showDailyTrimmedMean != undefined) { chart.showDailyTrimmedMean = details.showDailyTrimmedMean; }
      if (details.showDailyMin != undefined) { chart.showDailyMin = details.showDailyMin; }
      if (details.showDailyMax != undefined) { chart.showDailyMax = details.showDailyMax; }
      if (details.showDailySum != undefined) { chart.showDailySum = details.showDailySum; }
    });
  }


  // Notify the host that a chart option changed, so it can re-persist its
  // "last used" snapshot. Skipped while a saved favorite's options are being
  // applied (that is a restore, not a user edit).
  private emitChartDetailsChanged(): void {
    this.chartDetailsChanged.emit(this.getChartDetails());
  }


  // Snapshot the current user-selected chart options into a ChartDetails bag for
  // persistence on a Chart Favorite. Per-chart values come from the first chart
  // (the only chart). Host-level options (timezone,
  // readings, dark mode) are added by the host, which owns them.
  getChartDetails(): ChartDetails {
    const chart = this.chartInfos[0];
    return {
      echartType: this.echartType,
      lineXMode: this.lineXMode,
      scatterXSeries: [ ...this.scatterXSeries ],
      scatterYSeries: [ ...this.scatterYSeries ],
      overlayIndependentYAxis: this.overlayIndependentYAxis,
      leftAxisReadingType: this.leftAxisReadingType,
      manualScaleByAxis: this.cloneScaleMap(this.manualScaleByAxis),
      candlestickBucket: this.candlestickBucket,
      polarBucketDegrees: this.polarBucketDegrees,
      colorScheme: [ ...this.colorScheme ],
      hiddenSeries: this.hiddenSeriesNames(),
      seriesTypes: { ...this.seriesTypeOverrides },
      aggregateGroupMode: this.aggregateGroupMode,
      dailyGroupMode: this.dailyGroupMode,
      yMinIgnore: chart != undefined ? chart.yMinIgnore : '',
      yMaxIgnore: chart != undefined ? chart.yMaxIgnore : '',
      showAverage: chart != undefined ? chart.showAverage : undefined,
      showMedian: chart != undefined ? chart.showMedian : undefined,
      showTrimmedMean: chart != undefined ? chart.showTrimmedMean : undefined,
      showSum: chart != undefined ? chart.showSum : undefined,
      showDailyAverage: chart != undefined ? chart.showDailyAverage : undefined,
      showDailyMedian: chart != undefined ? chart.showDailyMedian : undefined,
      showDailyTrimmedMean: chart != undefined ? chart.showDailyTrimmedMean : undefined,
      showDailyMin: chart != undefined ? chart.showDailyMin : undefined,
      showDailyMax: chart != undefined ? chart.showDailyMax : undefined,
      showDailySum: chart != undefined ? chart.showDailySum : undefined
    };
  }


  // Legend names of the series the user has hidden (for persisting on a favorite).
  private hiddenSeriesNames(): string[] {
    const selected = this.legendSelected[0];
    if (selected == undefined) {
      return [];
    }
    return Object.keys(selected)
      .filter(name => selected[name] === false && name !== ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME);
  }


  // Build a CSV of the data series currently shown in the chart(s) for export.
  // Layout mirrors the backend readings export: leading CreatedDate/UUID/Name/Tag
  // columns, then a column per reading type for the raw per-sensor series (one
  // row per timestamp+sensor, sensor identity filled in). The client-computed
  // aggregate and running-sum series are added as their own columns: aggregate
  // rows have blank UUID/Name/Tag; sum rows carry the source sensor with a
  // "<sensor> (sum)" Name. Only legend-visible series are included and manual
  // y-scaling is ignored (every point is exported). Returns '' when empty.
  getChartExportCsv(): string {
    const leadColumns = ['CreatedDate', 'UUID', 'Name', 'Tag'];
    const valueColumns: string[] = [];
    const addColumn = (col: string) => { if (!valueColumns.includes(col)) { valueColumns.push(col); } };

    interface ExportRow { createdDate: string; sortMs: number; uuid: string; name: string; tag: string; rank: number; values: { [col: string]: string }; }
    const rows = new Map<string, ExportRow>();
    const getRow = (key: string, iso: string, ms: number, uuid: string, name: string, tag: string, rank: number): ExportRow => {
      let row = rows.get(key);
      if (row == undefined) {
        row = { createdDate: iso, sortMs: ms, uuid, name, tag, rank, values: {} };
        rows.set(key, row);
      }
      return row;
    };

    // Readings drop their per-reading Tag once charted; recover it by uuid+time.
    const tagByKey = new Map<string, string>();
    (this.sensorReadings ?? []).forEach(rd => {
      const key = rd.uuid + '|' + new Date(rd.createdDate).getTime();
      if (!tagByKey.has(key) && rd.tag != undefined && rd.tag !== '') {
        tagByKey.set(key, rd.tag);
      }
    });

    const sensorNameOf = (uuid: string): string => {
      const sensor = (this.sensors ?? []).find(s => s.uuid === uuid);
      return (sensor != undefined && sensor.name != undefined && sensor.name !== '') ? sensor.name : uuid;
    };
    const isoOf = (pointName: any): { iso: string; ms: number } => {
      const d = (pointName instanceof Date) ? pointName : new Date(pointName);
      return { iso: moment(d).format('YYYY-MM-DDTHH:mm:ss'), ms: d.getTime() };
    };

    this.chartInfos.forEach((chart, chartIndex) => {
      (chart.chartData ?? []).forEach((data: any) => {
        const points: { name: any; value: any }[] = data.series ?? [];
        if (points.length === 0 || !this.seriesVisible(chartIndex, data.name)) {
          return;
        }

        const isAggregate = data.isAggregate === true;
        const isRaw = !isAggregate && data.uuid != undefined;

        if (isRaw) {
          const type = this.readingTypeOfSeries(data.name);
          const suffix = data.name.trim().endsWith('(o)') ? '(orig)' : '(cal)';
          const valueCol = type + suffix;
          addColumn(valueCol);
          const uuid = data.uuid;
          const name = sensorNameOf(uuid);

          points.forEach(p => {
            const { iso, ms } = isoOf(p.name);
            const row = getRow(iso + '|' + uuid, iso, ms, uuid, name, tagByKey.get(uuid + '|' + ms) ?? '', 0);
            row.values[valueCol] = String(p.value);
          });

          // Running (cumulative) sum line for this raw series, when enabled and
          // its legend entry is visible.
          if (chart.showSum && this.seriesVisible(chartIndex, data.name + ' (sum)')) {
            const sumCol = valueCol + ' Sum';
            addColumn(sumCol);
            const sumName = name + ' (sum)';
            let runningTotal = 0;
            points
              .map(p => ({ ...isoOf(p.name), v: Number(p.value) }))
              .filter(p => !isNaN(p.ms) && !isNaN(p.v))
              .sort((a, b) => a.ms - b.ms)
              .forEach(p => {
                runningTotal += p.v;
                const row = getRow(p.iso + '|' + uuid + '|sum', p.iso, p.ms, uuid, sumName, '', 2);
                row.values[sumCol] = String(runningTotal);
              });
          }
        } else if (isAggregate) {
          const valueCol = data.name; // full aggregate series name as its column
          addColumn(valueCol);
          points.forEach(p => {
            const { iso, ms } = isoOf(p.name);
            const row = getRow(iso + '|agg', iso, ms, '', '', '', 1);
            row.values[valueCol] = String(p.value);
          });
        }
        // Other series (e.g. host overlay markers) are skipped.
      });
    });

    if (rows.size === 0) {
      return '';
    }

    const header = leadColumns.concat(valueColumns);
    const lines: string[] = [header.map(h => this.csvField(h)).join(',')];

    Array.from(rows.values())
      .sort((a, b) => (a.sortMs - b.sortMs) || (a.rank - b.rank) || a.uuid.localeCompare(b.uuid))
      .forEach(r => {
        const cells = [r.createdDate, r.uuid, r.name, r.tag].concat(valueColumns.map(c => r.values[c] ?? ''));
        lines.push(cells.map(c => this.csvField(c)).join(','));
      });

    return lines.join('\r\n') + '\r\n';
  }


  // Whether a series (by its ECharts name) is currently shown in a chart's
  // legend. Absent from the map = never toggled = visible.
  private seriesVisible(chartIndex: number, name: string): boolean {
    const selected = this.legendSelected[chartIndex];
    if (selected == undefined || selected[name] == undefined) {
      return true;
    }
    return selected[name] === true;
  }


  // Quote a CSV field only when it contains a comma, quote or newline.
  private csvField(value: string): string {
    const s = (value == undefined) ? '' : String(value);
    return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }


  updateChartData(): void {
    if (this.sensors != undefined && this.sensorReadingTypes != undefined) {

      this.chartInfos.forEach(chart => {
        chart.chartData = [];

        chart.dataTypes.forEach(datatype => {

          // Per-sensor series for this reading type, collected so we can also
          // build cross-sensor aggregate (average/median/trimmed-mean) series.
          // The sensor uuid travels with each series so the aggregate builder can
          // partition them by sensor group.
          const perSensorSeries: PerSensorSeries[] = [];

          this.sensors.forEach(sensor => {

            const legend = (sensor.name != undefined && sensor.name != '') ? sensor.name : sensor.uuid ;

            if (this.includeCalValues) {
              var sensorReadings = this.sensorReadings.filter(s => s.uuid == sensor.uuid && s.type == datatype.type && s.value != "nan" && s.value != "NaN")
                                                      .map(a => ({ name: new Date(a.createdDate), value: a.value }));

              if (chart.yMinIgnore != undefined && chart.yMinIgnore.toString() != '') {
                sensorReadings = sensorReadings.filter(s => Number(s.value) > Number(chart.yMinIgnore));
              }

              if (chart.yMaxIgnore != undefined && chart.yMaxIgnore.toString() != '') {
                sensorReadings = sensorReadings.filter(s => Number(s.value) < Number(chart.yMaxIgnore));
              }

              const newData = {
                name: datatype.type + ', ' + legend + ' (c)',
                series: sensorReadings,
                uuid: sensor.uuid
              };
              if (newData.series.length > 0) {
                chart.chartData.push(newData);
                chart.chartData = [...chart.chartData];
                perSensorSeries.push({ uuid: sensor.uuid, points: sensorReadings });
              }
            }

            if (this.includeOrigValues) {
              var sensorReadings = this.sensorReadings.filter(s => s.uuid == sensor.uuid && s.type == datatype.type && s.origValue != "nan" && s.origValue != "NaN")
                                                      .map(a => ({ name: new Date(a.createdDate), value: a.origValue }));

              if (chart.yMinIgnore != undefined && chart.yMinIgnore.toString() != '') {
                sensorReadings = sensorReadings.filter(s => Number(s.value) > Number(chart.yMinIgnore));
              }

              if (chart.yMaxIgnore != undefined && chart.yMaxIgnore.toString() != '') {
                sensorReadings = sensorReadings.filter(s => Number(s.value) < Number(chart.yMaxIgnore));
              }

              const newData = {
                name: datatype.type + ', ' + legend + ' (o)',
                series: sensorReadings,
                uuid: sensor.uuid
              };
              if (newData.series.length > 0) {
                chart.chartData.push(newData);
                chart.chartData = [...chart.chartData];
                perSensorSeries.push({ uuid: sensor.uuid, points: sensorReadings });
              }
            }

          });

          this.addAggregateSeries(chart, datatype.type, perSensorSeries);

          if (this.dailySeriesApplicable) {
            this.addDailySeries(chart, datatype.type, perSensorSeries);
          }
        });

        // Append any host-provided overlay series (e.g. event-action markers).
        if (this.overlaySeries != undefined && this.overlaySeries.length > 0) {
          this.overlaySeries.forEach(overlay => {
            if (overlay != undefined && overlay.series != undefined && overlay.series.length > 0) {
              chart.chartData.push({ name: overlay.name, series: overlay.series } as any);
            }
          });
          chart.chartData = [...chart.chartData];
        }
      });

      this.buildEchartOptions();
    }
  }


  // Build cross-sensor aggregate series (average / median / trimmed-mean) for a
  // single reading type and append them to the chart's data. In the default
  // 'readingType' mode this produces one set of aggregates over every selected
  // sensor; in 'groupAndReadingType' mode it produces one set per sensor group.
  private addAggregateSeries(chart: ChartInfo, readingType: string, perSensorSeries: PerSensorSeries[]): void {
    const anyAggregate = chart.showAverage || chart.showMedian || chart.showTrimmedMean;
    if (!anyAggregate || this.sensors == undefined || this.sensors.length <= 1) {
      return;
    }

    const withData = perSensorSeries.filter(s => s != undefined && s.points != undefined && s.points.length > 0);

    this.bucketSeriesByGroup(withData, this.groupAggregates).forEach(bucket => {
      this.pushAggregateSeriesForBucket(chart, readingType, bucket.label, bucket.series);
    });
  }


  // Partition the per-sensor series into the buckets each aggregate is computed
  // over. One unlabeled bucket (all sensors) in 'by Reading Type' mode; one per
  // selected sensor group in 'by Group & Reading Type' mode, matching sensors to
  // groups by uuid the same way buildTimeSincePlantingOption() does. A sensor in
  // two selected groups contributes to both; a sensor in none lands in a trailing
  // 'Ungrouped' bucket so it is not silently dropped.
  private bucketSeriesByGroup(perSensorSeries: PerSensorSeries[], groupByGroups: boolean): { label: string; series: { name: any; value: any }[][] }[] {
    if (!groupByGroups) {
      return [ { label: '', series: perSensorSeries.map(s => s.points) } ];
    }

    const buckets: { label: string; series: { name: any; value: any }[][] }[] = [];
    const grouped = new Set<PerSensorSeries>();

    (this.sensorGroups ?? []).forEach(group => {
      const matches = perSensorSeries.filter(s =>
        group.sensors != undefined && group.sensors.some(sensor => sensor.uuid === s.uuid));

      matches.forEach(s => grouped.add(s));

      if (matches.length > 0) {
        buckets.push({ label: group.name, series: matches.map(s => s.points) });
      }
    });

    const ungrouped = perSensorSeries.filter(s => !grouped.has(s));
    if (ungrouped.length > 0) {
      buckets.push({
        label: ChartReadingsEchartModalComponent.UNGROUPED_LABEL,
        series: ungrouped.map(s => s.points)
      });
    }

    return buckets;
  }


  // Compute and append the aggregate series for one bucket of sensor series.
  // Because sensors may report on different timestamps, each series is linearly
  // interpolated onto the union of all timestamps before the statistic is
  // computed. `bucketLabel` is '' for the all-sensor aggregate and the sensor
  // group name when aggregating per group.
  private pushAggregateSeriesForBucket(chart: ChartInfo, readingType: string, bucketLabel: string,
                                       series: { name: any; value: any }[][]): void {
    // Need at least two series to aggregate across sensors. A single-sensor group
    // therefore contributes no aggregate line (it would just copy its one sensor).
    if (series.length < 2) {
      return;
    }

    // Prefix shared by this bucket's series names. Kept comma-separated so
    // readingTypeOfSeries() still recovers the reading type from the text before
    // the first comma, and so 'by Reading Type' names are unchanged from before
    // this option existed.
    const namePrefix = bucketLabel !== '' ? readingType + ', ' + bucketLabel : readingType;

    // Normalize each series to sorted [timestamp, value] pairs.
    const normalized = series.map(s =>
      s.map(p => ({
          t: (p.name instanceof Date ? p.name : new Date(p.name)).getTime(),
          v: Number(p.value)
        }))
       .filter(p => !isNaN(p.t) && !isNaN(p.v))
       .sort((a, b) => a.t - b.t)
    ).filter(s => s.length > 0);

    if (normalized.length < 2) {
      return;
    }

    // Union of all timestamps across the sensors, ascending.
    const timestampSet = new Set<number>();
    normalized.forEach(s => s.forEach(p => timestampSet.add(p.t)));
    const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

    const averagePoints: { name: Date; value: number }[] = [];
    const medianPoints: { name: Date; value: number }[] = [];
    const trimmedMeanPoints: { name: Date; value: number }[] = [];

    timestamps.forEach(t => {
      // Interpolated value from each sensor at this timestamp (only sensors
      // whose data range covers t contribute; no extrapolation).
      const values: number[] = [];
      normalized.forEach(s => {
        const value = this.interpolateAt(s, t);
        if (value != null) {
          values.push(value);
        }
      });

      // Require coverage from at least two sensors for a meaningful aggregate.
      if (values.length < 2) {
        return;
      }

      const date = new Date(t);
      if (chart.showAverage) {
        averagePoints.push({ name: date, value: this.mean(values) });
      }
      if (chart.showMedian) {
        medianPoints.push({ name: date, value: this.median(values) });
      }
      if (chart.showTrimmedMean) {
        trimmedMeanPoints.push({ name: date, value: this.trimmedMean(values, 0.1) });
      }
    });

    // bucketLabel travels with each aggregate so TimeSincePlanting mode can find
    // the aggregate's group (and thus its plantDate / day-0). '' = all-sensor.
    if (chart.showAverage && averagePoints.length > 0) {
      chart.chartData.push({ name: namePrefix + ', Average', series: averagePoints, isAggregate: true, aggregateGroupLabel: bucketLabel } as any);
    }
    if (chart.showMedian && medianPoints.length > 0) {
      chart.chartData.push({ name: namePrefix + ', Median', series: medianPoints, isAggregate: true, aggregateGroupLabel: bucketLabel } as any);
    }
    if (chart.showTrimmedMean && trimmedMeanPoints.length > 0) {
      chart.chartData.push({ name: namePrefix + ', Trimmed-Mean', series: trimmedMeanPoints, isAggregate: true, aggregateGroupLabel: bucketLabel } as any);
    }
    chart.chartData = [...chart.chartData];
  }


  // Build per-day statistic series for a reading type and append them. Pools the
  // day's readings (across the selected sensors, or per group when
  // dailyGroupMode = groupAndReadingType) and computes a value for each day.
  private addDailySeries(chart: ChartInfo, readingType: string, perSensorSeries: PerSensorSeries[]): void {
    const anyDaily = chart.showDailyAverage || chart.showDailyMedian || chart.showDailyTrimmedMean
      || chart.showDailyMin || chart.showDailyMax || chart.showDailySum;
    if (!anyDaily) {
      return;
    }

    const withData = perSensorSeries.filter(s => s != undefined && s.points != undefined && s.points.length > 0);

    this.bucketSeriesForDaily(withData).forEach(bucket => {
      this.pushDailySeriesForBucket(chart, readingType, bucket.label, bucket.series);
    });
  }


  // Partition the per-sensor series into the buckets each daily series is
  // computed over, per dailyGroupMode. 'readingTypeAndSensor' → one bucket per
  // individual sensor (pooling that sensor's calibrated + original entries),
  // labeled with the sensor name. The other two modes reuse bucketSeriesByGroup.
  private bucketSeriesForDaily(perSensorSeries: PerSensorSeries[]): { label: string; series: { name: any; value: any }[][] }[] {
    if (this.dailyGroupMode === 'readingTypeAndSensor') {
      const byUuid = new Map<string, { name: any; value: any }[][]>();
      perSensorSeries.forEach(s => {
        if (!byUuid.has(s.uuid)) {
          byUuid.set(s.uuid, []);
        }
        byUuid.get(s.uuid).push(s.points);
      });
      return Array.from(byUuid.entries()).map(([uuid, seriesList]) => ({
        label: this.sensorLabelForUuid(uuid),
        series: seriesList
      }));
    }

    return this.bucketSeriesByGroup(perSensorSeries, this.groupDaily);
  }


  // Display name for a sensor uuid (matches the legend naming in updateChartData).
  private sensorLabelForUuid(uuid: string): string {
    const sensor = (this.sensors ?? []).find(s => s.uuid === uuid);
    return (sensor != undefined && sensor.name != undefined && sensor.name !== '') ? sensor.name : uuid;
  }


  // Compute and append the daily series for one bucket. The bucket's readings are
  // pooled and grouped by local calendar day. Average/Median/Trimmed-Mean/Min/Max
  // become a horizontal segment per day (start-of-day + end-of-day points at that
  // day's value). Sum becomes a within-day cumulative line (running total at each
  // timestamp, reset each day, with a gap between days); it is tagged isDailySum
  // so buildStandardOption places it on the dedicated Sum axis.
  private pushDailySeriesForBucket(chart: ChartInfo, readingType: string, bucketLabel: string,
                                   series: { name: any; value: any }[][]): void {
    const valuesByDay = new Map<string, number[]>();
    const pointsByDay = new Map<string, { t: number; v: number }[]>();

    series.forEach(points => {
      points.forEach(p => {
        const date = p.name instanceof Date ? p.name : new Date(p.name);
        const t = date.getTime();
        const v = Number(p.value);
        if (isNaN(t) || isNaN(v)) {
          return;
        }
        const day = moment(date).format('YYYY-MM-DD');
        if (!valuesByDay.has(day)) {
          valuesByDay.set(day, []);
          pointsByDay.set(day, []);
        }
        valuesByDay.get(day).push(v);
        pointsByDay.get(day).push({ t, v });
      });
    });

    if (valuesByDay.size === 0) {
      return;
    }

    const days = Array.from(valuesByDay.keys()).sort();
    const namePrefix = bucketLabel !== '' ? readingType + ', ' + bucketLabel : readingType;

    // Two points per day (start & end of day) at the day's statistic → a
    // horizontal segment per day.
    const buildFlat = (stat: (vals: number[]) => number): { name: Date; value: number }[] => {
      const pts: { name: Date; value: number }[] = [];
      days.forEach(day => {
        const value = stat(valuesByDay.get(day));
        pts.push(
          { name: moment(day, 'YYYY-MM-DD').startOf('day').toDate(), value },
          { name: moment(day, 'YYYY-MM-DD').endOf('day').toDate(), value }
        );
      });
      return pts;
    };

    const pushSeries = (suffix: string, pts: { name: any; value: any }[], extra?: any) => {
      if (pts.length > 0) {
        chart.chartData.push({ name: namePrefix + ', ' + suffix, series: pts, isAggregate: true, aggregateGroupLabel: bucketLabel, ...(extra ?? {}) } as any);
      }
    };

    if (chart.showDailyAverage)     { pushSeries('Daily Average', buildFlat(v => this.mean(v))); }
    if (chart.showDailyMedian)      { pushSeries('Daily Median', buildFlat(v => this.median(v))); }
    if (chart.showDailyTrimmedMean) { pushSeries('Daily Trimmed-Mean', buildFlat(v => this.trimmedMean(v, 0.1))); }
    if (chart.showDailyMin)         { pushSeries('Daily Min', buildFlat(v => this.minOf(v))); }
    if (chart.showDailyMax)         { pushSeries('Daily Max', buildFlat(v => this.maxOf(v))); }

    if (chart.showDailySum) {
      const pts: { name: any; value: any }[] = [];
      days.forEach((day, di) => {
        const dayPoints = pointsByDay.get(day).slice().sort((a, b) => a.t - b.t);
        let runningTotal = 0;
        dayPoints.forEach(p => {
          runningTotal += p.v;
          pts.push({ name: new Date(p.t), value: runningTotal });
        });
        // Break the line between days so each day's cumulative reset is clear
        // ('-' becomes NaN in the option, which ECharts renders as a gap).
        if (di < days.length - 1) {
          pts.push({ name: moment(day, 'YYYY-MM-DD').endOf('day').toDate(), value: '-' });
        }
      });
      pushSeries('Daily Sum', pts, { isDailySum: true });
    }

    chart.chartData = [...chart.chartData];
  }


  // Linearly interpolate a sorted [{t, v}] series at timestamp t. Returns null
  // when t falls outside the series' range (no extrapolation).
  private interpolateAt(series: { t: number; v: number }[], t: number): number | null {
    if (series.length === 0 || t < series[0].t || t > series[series.length - 1].t) {
      return null;
    }

    // Binary search for the bracketing points.
    let lo = 0;
    let hi = series.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (series[mid].t === t) {
        return series[mid].v;
      } else if (series[mid].t < t) {
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    // hi is the last index with t' < t, lo the first with t' > t.
    const left = series[hi];
    const right = series[lo];
    if (left == undefined || right == undefined) {
      return left ? left.v : (right ? right.v : null);
    }

    const span = right.t - left.t;
    if (span === 0) {
      return left.v;
    }
    const ratio = (t - left.t) / span;
    return left.v + ratio * (right.v - left.v);
  }


  private mean(values: number[]): number {
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }


  // Ordinary least-squares linear fit of [x, y] points. Returns the slope,
  // intercept and coefficient of determination (R²), or null when a line cannot
  // be fit (fewer than 2 points, or no variance in x).
  private linearRegression(points: number[][]): { slope: number; intercept: number; r2: number } | null {
    const n = points.length;
    if (n < 2) {
      return null;
    }

    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    points.forEach(([x, y]) => {
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumXX += x * x;
    });

    const denom = n * sumXX - sumX * sumX;
    if (denom === 0) {
      return null; // vertical / no x variance
    }

    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;

    // R² = 1 - SS_res / SS_tot.
    const meanY = sumY / n;
    let ssRes = 0, ssTot = 0;
    points.forEach(([x, y]) => {
      const predicted = slope * x + intercept;
      ssRes += (y - predicted) * (y - predicted);
      ssTot += (y - meanY) * (y - meanY);
    });
    const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

    return { slope, intercept, r2 };
  }


  private median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }


  // Mean after discarding the given proportion of values from each tail.
  private trimmedMean(values: number[], proportion: number): number {
    const sorted = [...values].sort((a, b) => a - b);
    const cut = Math.floor(sorted.length * proportion);
    const trimmed = sorted.slice(cut, sorted.length - cut);
    const use = trimmed.length > 0 ? trimmed : sorted;
    return this.mean(use);
  }


  // Reduce-based min/max (avoids Math.min(...arr) spread limits on large arrays).
  private minOf(values: number[]): number {
    return values.reduce((m, v) => (v < m ? v : m), values[0]);
  }


  private maxOf(values: number[]): number {
    return values.reduce((m, v) => (v > m ? v : m), values[0]);
  }


  // Translate the ChartInfo/chartData structures into ECharts option objects.
  buildEchartOptions(): void {
    this.refreshScatterOptions();
    this.chartOptions = this.chartInfos.map((chart, i) => {
      let option: any;
      if (this.echartType === 'candlestick') {
        option = this.buildCandlestickOption(chart, i);
      } else if (this.echartType === 'polar') {
        option = this.buildPolarOption(chart, i);
      } else {
        option = this.buildStandardOption(chart, i);
      }
      // Size the reserved legend space to the longest actual label.
      this.adjustLegendWidth(option);
      // Minimal embeds (e.g. dashboard cards): drop axis titles and shrink the
      // grid padding so the chart fills its small tile.
      this.applyMinimalLayout(option);
      // Hovering a legend item (or a line) bolds that series and fades the rest.
      this.applyHoverEmphasis(option);
      return option;
    });

    // If the user switched to a mode that can't carry labels, leave edit mode so
    // stale drag handles / the editor don't linger.
    if (this.labelsEditMode && !this.annotationsApplicable) {
      this.exitLabelEditing();
    }
  }


  // Make the hovered series stand out among many overlapping lines: on hover
  // (legend hoverLink is on by default, and direct line hover) the series is
  // emphasized -- bolder line + soft shadow -- while focus: 'series' blurs every
  // other series to a faint gray. Applied centrally so every chart type gets it;
  // style keys that don't apply to a given series type are ignored by ECharts.
  private applyHoverEmphasis(option: any): void {
    if (option == undefined || option.series == undefined) {
      return;
    }

    const series = Array.isArray(option.series) ? option.series : [option.series];
    series.forEach((s: any) => {
      if (s == undefined) {
        return;
      }
      // The annotation series manages its own (disabled) emphasis/blur so labels
      // don't fade when another series is hovered — leave it alone.
      if (s.name === ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME) {
        return;
      }
      // Don't clobber a series that already defines its own emphasis/blur.
      if (s.emphasis == undefined) {
        s.emphasis = {
          focus: 'series',
          blurScope: 'coordinateSystem',
          lineStyle: { width: 4, shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.45)' },
          itemStyle: { borderWidth: 2 }
        };
      }
      if (s.blur == undefined) {
        s.blur = {
          lineStyle: { opacity: 0.12 },
          itemStyle: { opacity: 0.12 },
          areaStyle: { opacity: 0.05 }
        };
      }
    });
  }


  // dataZoom config for a chart, seeded with the saved zoom range (if any) so
  // the current time selection is retained across option rebuilds.
  private dataZoom(chartIndex: number): any[] {
    // Minimal mode: no zoom sliders (nor inside-zoom) — just a static chart.
    if (this.minimal) {
      return [];
    }

    const range = this.zoomRanges[chartIndex];
    const start = range ? range.start : 0;
    const end = range ? range.end : 100;

    const zooms: any[] = [
      { type: 'inside', xAxisIndex: 0, start: start, end: end },
      { type: 'slider', xAxisIndex: 0, start: start, end: end }
    ];

    if (this.showYAxisSlider) {
      // Vertical slider on the left scaling the (left) y-axis min/max. Retains
      // its range across rebuilds. filterMode 'none' scales the view without
      // discarding points outside the window.
      const yRange = this.yZoomRanges[chartIndex];
      const yStart = yRange ? yRange.start : 0;
      const yEnd = yRange ? yRange.end : 100;
      zooms.push(
        { type: 'inside', yAxisIndex: 0, filterMode: 'none', start: yStart, end: yEnd },
        { type: 'slider', yAxisIndex: 0, filterMode: 'none', left: 5, start: yStart, end: yEnd }
      );
    }

    return zooms;
  }


  // Remember the zoom ranges whenever the user pans/zooms a chart. Reads the
  // instance's current dataZoom state so we can tell the x-axis and y-axis
  // sliders apart and retain each across option rebuilds.
  onChartDataZoom(chartIndex: number, _event: any): void {
    const instance = this.echartInstances[chartIndex];
    if (instance == undefined) {
      return;
    }

    const dataZooms: any[] = instance.getOption()?.dataZoom ?? [];
    dataZooms.forEach(dz => {
      if (dz == undefined || dz.start == undefined || dz.end == undefined) {
        return;
      }
      const targetsY = dz.yAxisIndex != undefined
        && !(Array.isArray(dz.yAxisIndex) && dz.yAxisIndex.length === 0);
      if (targetsY) {
        this.yZoomRanges[chartIndex] = { start: dz.start, end: dz.end };
      } else {
        this.zoomRanges[chartIndex] = { start: dz.start, end: dz.end };
      }
    });

    // Keep the drag handles glued to their data points as the view scales/pans.
    if (this.labelsEditMode && chartIndex === 0) {
      this.renderDragHandles();
    }
  }


  // Build a line/bar/scatter option with a time x-axis.
  // Build one independent value axis per reading type (plus a dedicated 'Sum'
  // axis when there are cumulative sum lines). The user-selected axis
  // (leftAxisReadingType) is shown on the left; the rest stack on the right, each
  // offset so they don't overlap. Each axis autoscales (scale: true, no min/max)
  // so it uses the whole vertical space. Right-side titles are bold, slightly
  // smaller (so adjacent titles don't overlap) and get triggerEvent so a click
  // promotes that axis to the left (see onChartClick). Falls back to the first
  // axis when nothing is selected or the saved selection is no longer present.
  // Returns the index of the Sum axis within the yAxis array (-1 when none) so
  // the caller can plot the sum series on it.
  private buildIndependentYAxes(readingTypes: string[], axisGap: number, hasSumLines: boolean = false):
      { yAxis: any[]; gridRight: number; sumAxisIndex: number; keys: string[] } {
    // Candidate axes, in array order: one per reading type (palette-colored),
    // then the gray 'Sum' axis when present. Any of them may be the left axis.
    const axisDefs: { key: string; color: string }[] = readingTypes.map((type, i) => ({
      key: type,
      color: this.colorScheme[i % this.colorScheme.length]
    }));
    if (hasSumLines) {
      axisDefs.push({ key: 'Sum', color: '#888888' });
    }

    const leftKey = axisDefs.some(a => a.key === this.leftAxisReadingType)
      ? this.leftAxisReadingType
      : axisDefs[0].key;

    let rightIndex = 0; // 0-based index among the right-side axes
    const yAxis = axisDefs.map(def => {
      const onLeft = def.key === leftKey;
      return {
        type: 'value',
        name: this.axisDisplayName(def.key),
        scale: true,
        // Manual scaling for this axis (undefined = autoscale that end).
        min: this.axisManualMin(def.key),
        max: this.axisManualMax(def.key),
        position: onLeft ? 'left' : 'right',
        offset: onLeft ? 0 : (rightIndex++) * axisGap,
        triggerEvent: true,                                                       // enable axis-title (move) and axis-value (scale) clicks
        nameTextStyle: onLeft ? undefined : { fontWeight: 'bold', fontSize: 11 }, // right titles look clickable, shrunk 1pt to avoid overlap
        axisLine: {
          show: true,
          lineStyle: { color: def.color }
        }
      };
    });

    const sumAxisIndex = hasSumLines ? yAxis.length - 1 : -1;

    // Reserve room on the right for the stacked axes (outermost sits at
    // (rightCount - 1) * axisGap, plus ~40px for its labels).
    const rightCount = axisDefs.length - 1;
    const gridRight = 40 + Math.max(0, rightCount - 1) * axisGap;
    return { yAxis, gridRight, sumAxisIndex, keys: axisDefs.map(d => d.key) };
  }


  private buildStandardOption(chart: ChartInfo, chartIndex: number): EChartsOption {
    // Line chart with MinOfDay mode → overlay each day on a time-of-day x-axis.
    if (this.echartType === 'line' && this.lineXMode === 'minOfDay' && this.readingsSpanMultipleDays) {
      return this.buildMinOfDayOption(chart, chartIndex);
    }

    // Line chart with timeSincePlanting mode → x = days since each group's plantDate.
    if (this.isTimeSincePlantingActive) {
      return this.buildTimeSincePlantingOption(chart, chartIndex);
    }

    // Scatter with an explicit non-Time X series → correlation (value vs value).
    if (this.isScatterCorrelationActive) {
      return this.buildScatterCorrelationOption(chart, chartIndex);
    }

    // Scatter with X = Time (and specific Y series selected) → time vs value,
    // but limited to the selected Y series.
    if (this.echartType === 'scatter'
        && this.scatterYSeries.length > 0) {
      chart = this.filterChartToSeries(chart, this.scatterYSeries);
    }

    // Determine the reading type of each data series (the series name is built
    // as "<type>, <legend> (c|o)", so the type is the text before the comma).
    const seriesTypes = chart.chartData.map(data => this.readingTypeOfSeries(data.name));

    // Ordered, unique list of reading types on this chart.
    const readingTypes: string[] = [];
    seriesTypes.forEach(type => {
      if (!readingTypes.includes(type)) {
        readingTypes.push(type);
      }
    });

    // Give each reading type its own independent Y-axis only when the chart has
    // more than one reading type and the user has opted in.
    const useIndependentAxes = this.overlayIndependentYAxis
      && readingTypes.length > 1;

    // Are there any (non-aggregate) sum lines to draw? If so they get their own
    // dedicated right-side y-axis so their large cumulative values don't compress
    // the reading series.
    const hasSumLines = chart.showSum && chart.chartData.some(d => !d.isAggregate && d.series.length > 0);
    // Daily-Sum series (per-day cumulative) also ride the dedicated Sum axis.
    const hasDailySum = chart.chartData.some((d: any) => d.isDailySum === true);
    const needsSumAxis = hasSumLines || hasDailySum;

    let yAxis: any;
    // Reserve extra space on the left for the vertical y-axis slider when shown.
    let gridLeft = 60 + this.reservedYSliderWidth;
    let gridRight = 40;
    const axisGap = 60;
    // Index of the "Sum" axis within yAxis (-1 when there are no sum lines).
    let sumAxisIndex = -1;

    // Index of each reading series' primary y-axis.
    const seriesAxisIndex = (index: number) =>
      useIndependentAxes ? readingTypes.indexOf(seriesTypes[index]) : 0;

    if (useIndependentAxes) {
      // The Sum axis (when present) is built as another selectable independent
      // axis, so it can be clicked and moved to the left like the reading types.
      const built = this.buildIndependentYAxes(readingTypes, axisGap, needsSumAxis);
      yAxis = built.yAxis;
      gridRight = built.gridRight;
      sumAxisIndex = built.sumAxisIndex;
      this.axisKeysByChart[chartIndex] = built.keys;
    } else {
      // triggerEvent lets the user click the axis values to set manual scaling;
      // the '*' suffix and min/max come from any manual scaling on that axis.
      const valueAxis: any = {
        type: 'value',
        name: this.axisDisplayName('Value'),
        scale: true,
        min: this.primaryYMin(chart),
        max: this.primaryYMax(chart),
        triggerEvent: true
      };

      if (needsSumAxis) {
        // Two axes (the reading "Value" axis and the cumulative "Sum" axis).
        // Either can be moved to the left by clicking its title; default is
        // Value on the left, Sum on the right. Both get triggerEvent, and
        // whichever sits on the right is bolded (shrunk 1pt) to read as clickable.
        const sumOnLeft = this.leftAxisReadingType === 'Sum';
        const sumAxis: any = {
          type: 'value',
          name: this.axisDisplayName('Sum'),
          scale: true,
          min: this.axisManualMin('Sum'),
          max: this.axisManualMax('Sum'),
          triggerEvent: true,
          axisLine: { show: true, lineStyle: { color: '#888888' } }
        };

        valueAxis.position = sumOnLeft ? 'right' : 'left';
        valueAxis.offset = 0;
        if (sumOnLeft) { valueAxis.nameTextStyle = { fontWeight: 'bold', fontSize: 11 }; }

        sumAxis.position = sumOnLeft ? 'left' : 'right';
        sumAxis.offset = 0;
        if (!sumOnLeft) { sumAxis.nameTextStyle = { fontWeight: 'bold', fontSize: 11 }; }

        yAxis = [ valueAxis, sumAxis ];
        sumAxisIndex = 1;
        gridRight = 40 + axisGap; // room for the single right-side axis and its labels
        this.axisKeysByChart[chartIndex] = [ 'Value', 'Sum' ];
      } else {
        yAxis = [ valueAxis ];
        this.axisKeysByChart[chartIndex] = [ 'Value' ];
      }
    }

    const series = chart.chartData.map((data, index) => ({
      name: data.name,
      ...this.seriesStyle(data.name),
      // Lets a right-click on the line itself (not just a point) open the
      // per-series chart type menu.
      triggerLineEvent: true,
      smooth: false,
      // Daily-Sum lands on the dedicated Sum axis; everything else on its
      // reading type's (or the single Value) axis.
      yAxisIndex: ((data as any).isDailySum === true && sumAxisIndex >= 0) ? sumAxisIndex : seriesAxisIndex(index),
      itemStyle: {
        color: this.colorScheme[index % this.colorScheme.length]
      },
      data: data.series.map(point => [
        (point.name instanceof Date ? point.name : new Date(point.name)).getTime(),
        Number(point.value)
      ])
    }));

    // Per-series running (cumulative) sum lines (short-dashed), color-matched to
    // their source series, plotted on the dedicated Sum axis. Only for raw
    // reading series, not aggregate lines.
    if (hasSumLines) {
      chart.chartData.forEach((data, index) => {
        if (data.isAggregate) {
          return;
        }

        const color = this.colorScheme[index % this.colorScheme.length];
        let runningTotal = 0;
        const sumData = data.series
          .map(point => ({
            t: (point.name instanceof Date ? point.name : new Date(point.name)).getTime(),
            v: Number(point.value)
          }))
          .filter(p => !isNaN(p.t) && !isNaN(p.v))
          .sort((a, b) => a.t - b.t)
          .map(p => {
            runningTotal += p.v;
            return [p.t, runningTotal];
          });

        if (sumData.length > 0) {
          series.push({
            name: data.name + ' (sum)',
            type: 'line',
            showSymbol: false,
            symbolSize: 0,
            smooth: false,
            yAxisIndex: sumAxisIndex,
            lineStyle: { color: color, type: [4, 4], width: 2 },
            itemStyle: { color: color },
            data: sumData
          } as any);
        }
      });
    }

    // Overlay the favorite's labels as a dedicated invisible annotation series
    // (data-anchored markLine/markArea/markPoint, so they move/scale with zoom).
    const annotationSeries = this.buildAnnotationSeries(chartIndex);
    if (annotationSeries != undefined) {
      series.push(annotationSeries as any);
    }

    // When the annotation series is present, drive the legend from an explicit
    // list so the '__annotations__' series never shows up as a legend entry.
    const legendOption = this.legend(chartIndex);
    if (annotationSeries != undefined) {
      legendOption.data = (series as any[])
        .map(s => s.name)
        .filter(n => n != undefined && n !== ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME);
    }

    return {
      animation: false,
      backgroundColor: this.backgroundColor(),
      darkMode: this.darkBackground,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      toolbox: this.toolbox(),
      legend: legendOption,
      grid: {
        left: gridLeft,
        right: gridRight + this.reservedLegendWidth,
        top: 40,
        bottom: 70
      },
      xAxis: {
        type: 'time',
        name: 'Date-Time',
        nameLocation: 'middle',
        nameGap: 30
      },
      yAxis: yAxis,
      dataZoom: this.dataZoom(chartIndex),
      series: series as any
    };
  }


  // Build a line option that overlays each calendar day on a common time-of-day
  // x-axis (minutes 0-1440, labeled HH:mm). Each reading series is split into one
  // line per day; all of a given series' (sensor's) per-day lines share that
  // series' color so sensors are distinguishable at a glance.
  //
  // Aggregate Series (Average/Median/Trimmed-Mean) compute a "typical day": for
  // each series, at each time-of-day, the statistic ACROSS that series' days,
  // drawn as a bold line overlaid on the per-day lines. (Sum does not apply.)
  private buildMinOfDayOption(chart: ChartInfo, chartIndex: number): EChartsOption {
    const series: any[] = [];
    const anyAggregate = chart.showAverage || chart.showMedian || chart.showTrimmedMean;

    // Determine the reading type of each data series (name = "<type>, <legend> (c|o)").
    const seriesTypes = chart.chartData.map(data => this.readingTypeOfSeries(data.name));
    const readingTypes: string[] = [];
    seriesTypes.forEach(type => {
      if (!readingTypes.includes(type)) {
        readingTypes.push(type);
      }
    });

    // Give each reading type its own independent Y-axis only when the chart has
    // more than one reading type and the user has opted in.
    const useIndependentAxes = this.overlayIndependentYAxis
      && readingTypes.length > 1;

    // The y-axis index for a chartData entry (0 unless independent axes are on).
    const axisIndexFor = (data: any) =>
      useIndependentAxes ? readingTypes.indexOf(this.readingTypeOfSeries(data.name)) : 0;

    chart.chartData.forEach((data, seriesIndex) => {
      if (data.isAggregate) {
        return;
      }

      // Group this series' points by calendar day → [minuteOfDay, value].
      const byDay = new Map<string, number[][]>();
      data.series.forEach(point => {
        const d = point.name instanceof Date ? point.name : new Date(point.name);
        const value = Number(point.value);
        if (isNaN(d.getTime()) || isNaN(value)) {
          return;
        }
        const m = moment(d);
        const day = m.format('YYYY-MM-DD');
        const minuteOfDay = m.hours() * 60 + m.minutes();
        if (!byDay.has(day)) {
          byDay.set(day, []);
        }
        byDay.get(day).push([minuteOfDay, value]);
      });

      const sortedDays = Array.from(byDay.keys()).sort();

      // All per-day lines for a given series (sensor) share that series' color.
      const color = this.colorScheme[seriesIndex % this.colorScheme.length];
      const yAxisIndex = axisIndexFor(data);

      // One line per day for this series.
      sortedDays.forEach(day => {
        const points = byDay.get(day).sort((a, b) => a[0] - b[0]);
        series.push({
          name: data.name + ' — ' + day,
          type: 'line',
          showSymbol: false,
          smooth: false,
          yAxisIndex: yAxisIndex,
          itemStyle: { color: color },
          lineStyle: { color: color },
          data: points
        });
      });

      // "Typical day" aggregate line(s) across this series' days.
      if (anyAggregate && sortedDays.length > 1) {
        this.addMinOfDayAggregateSeries(series, data.name, byDay, seriesIndex, chart, yAxisIndex);
      }
    });

    // Build the y-axis (one per reading type when independent axes are on).
    let yAxis: any;
    let gridRight = 40;
    const axisGap = 60;
    if (useIndependentAxes) {
      const built = this.buildIndependentYAxes(readingTypes, axisGap);
      yAxis = built.yAxis;
      gridRight = built.gridRight;
      this.axisKeysByChart[chartIndex] = built.keys;
    } else {
      yAxis = [
        {
          type: 'value',
          name: this.axisDisplayName('Value'),
          scale: true,
          min: this.primaryYMin(chart),
          max: this.primaryYMax(chart),
          triggerEvent: true
        }
      ];
      this.axisKeysByChart[chartIndex] = [ 'Value' ];
    }

    return {
      animation: false,
      backgroundColor: this.backgroundColor(),
      darkMode: this.darkBackground,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      toolbox: this.toolbox(),
      legend: this.legend(chartIndex),
      grid: {
        left: 60 + this.reservedYSliderWidth,
        right: gridRight + this.reservedLegendWidth,
        top: 40,
        bottom: 70
      },
      xAxis: {
        type: 'value',
        name: 'Time of Day',
        nameLocation: 'middle',
        nameGap: 30,
        min: 0,
        max: 1440,
        interval: 120,   // a tick every 2 hours
        axisLabel: {
          formatter: (value: number) => {
            const h = Math.floor(value / 60);
            const mm = value % 60;
            return (h < 10 ? '0' + h : '' + h) + ':' + (mm < 10 ? '0' + mm : '' + mm);
          }
        }
      },
      yAxis: yAxis,
      dataZoom: this.dataZoom(chartIndex),
      series: series as any
    };
  }


  // Build a line option whose x-axis is whole "days after planting". Day 0 is the
  // sensor's group plantDate. A sensor may belong to multiple selected groups, so
  // its readings are plotted once per group (relative to that group's plantDate),
  // each as its own line. Sensors with no group plantDate fall back to the
  // date-range start (dateRangeStart) as day 0.
  private buildTimeSincePlantingOption(chart: ChartInfo, chartIndex: number): EChartsOption {
    const series: any[] = [];
    let colorIndex = 0;

    chart.chartData.forEach(data => {
      // day-0 candidates: one entry per baseline this series is plotted against.
      const baselines: { label: string; day0: Date }[] = [];
      let appendBaselineLabel = false;

      if (data.isAggregate) {
        // Aggregate lines aren't tied to a single sensor, so previously they were
        // skipped here -- which dropped them from the chart entirely. Plot the one
        // cross-sensor line against a single day-0 resolved from its group.
        const day0 = this.aggregateDay0((data as any).aggregateGroupLabel);
        if (day0 != undefined) {
          baselines.push({ label: (data as any).aggregateGroupLabel ?? '', day0 });
        }
      } else {
        const uuid = (data as any).uuid as string;

        // Groups (with a plantDate) that contain this sensor.
        const groups = (this.sensorGroups ?? []).filter(g =>
          g.plantDate != undefined
          && g.sensors != undefined
          && g.sensors.some(s => s.uuid === uuid));

        // day-0 candidates: one entry per containing group; else a single fallback.
        if (groups.length > 0) {
          groups.forEach(g => baselines.push({ label: g.name, day0: new Date(g.plantDate) }));
          // A sensor in multiple groups is plotted once per group's plantDate, so
          // label each line to keep them distinct.
          appendBaselineLabel = true;
        } else if (this.dateRangeStart != undefined) {
          baselines.push({ label: 'Date Range Start', day0: new Date(this.dateRangeStart) });
        }
      }

      baselines.forEach(baseline => {
        const day0 = moment(baseline.day0);
        const points = data.series
          .map(point => {
            const d = point.name instanceof Date ? point.name : new Date(point.name);
            const value = Number(point.value);
            if (isNaN(d.getTime()) || isNaN(value)) {
              return null;
            }
            // Fractional days after planting (includes hours/minutes/seconds;
            // can be negative for pre-plant readings).
            const daysAfter = moment(d).diff(day0, 'days', true);
            return [daysAfter, value];
          })
          .filter(p => p != null)
          .sort((a, b) => a[0] - b[0]);

        if (points.length > 0) {
          const color = this.colorScheme[colorIndex % this.colorScheme.length];
          colorIndex++;
          series.push({
            // Append the baseline (group) label only for per-sensor lines split
            // across groups; aggregate names already carry their group.
            name: appendBaselineLabel ? data.name + ' — ' + baseline.label : data.name,
            type: 'line',
            showSymbol: false,
            smooth: false,
            itemStyle: { color: color },
            lineStyle: { color: color },
            data: points
          });
        }
      });
    });

    this.axisKeysByChart[chartIndex] = [ 'Value' ];

    return {
      animation: false,
      backgroundColor: this.backgroundColor(),
      darkMode: this.darkBackground,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      toolbox: this.toolbox(),
      legend: this.legend(chartIndex),
      grid: {
        left: 60 + this.reservedYSliderWidth,
        right: 40 + this.reservedLegendWidth,
        top: 40,
        bottom: 70
      },
      xAxis: {
        type: 'value',
        name: 'Days After Planting',
        nameLocation: 'middle',
        nameGap: 30,
        scale: true
      },
      yAxis: {
        type: 'value',
        name: this.axisDisplayName('Value'),
        scale: true,
        min: this.primaryYMin(chart),
        max: this.primaryYMax(chart),
        triggerEvent: true
      },
      dataZoom: this.dataZoom(chartIndex),
      series: series as any
    };
  }


  // Day-0 for an aggregate line in TimeSincePlanting mode. A per-group aggregate
  // (groupAndReadingType mode) uses its own group's plantDate; the all-sensor
  // aggregate uses the sole group's plantDate when unambiguous, otherwise the
  // date-range start -- mirroring the per-sensor fallback so the aggregate lines
  // up with the raw series. Returns undefined when no day-0 can be resolved.
  private aggregateDay0(aggregateGroupLabel: string): Date | undefined {
    const groupsWithPlant = (this.sensorGroups ?? []).filter(g => g.plantDate != undefined);

    if (aggregateGroupLabel != undefined && aggregateGroupLabel !== '') {
      const match = groupsWithPlant.find(g => g.name === aggregateGroupLabel);
      if (match != undefined) {
        return new Date(match.plantDate);
      }
    }

    // All-sensor aggregate (or an unmatched label): only unambiguous when a single
    // selected group carries a plantDate.
    if (groupsWithPlant.length === 1) {
      return new Date(groupsWithPlant[0].plantDate);
    }

    return this.dateRangeStart != undefined ? new Date(this.dateRangeStart) : undefined;
  }


  // For a MinOfDay series (its points grouped by day as [minuteOfDay, value]),
  // compute a "typical day" aggregate line ACROSS days: at each time-of-day, the
  // statistic over the days that cover that minute (linear interpolation, no
  // extrapolation). One bold line per enabled aggregate, color-matched to the
  // source series, appended to `series`.
  private addMinOfDayAggregateSeries(series: any[], seriesName: string,
      byDay: Map<string, number[][]>, seriesIndex: number, chart: ChartInfo, yAxisIndex: number = 0): void {

    // Each day's points as a sorted {t: minuteOfDay, v: value} array.
    const perDay = Array.from(byDay.values())
      .map(points => points
        .map(p => ({ t: p[0], v: p[1] }))
        .filter(p => !isNaN(p.t) && !isNaN(p.v))
        .sort((a, b) => a.t - b.t))
      .filter(d => d.length > 0);

    if (perDay.length < 2) {
      return;
    }

    // Union of minute-of-day values across days.
    const minuteSet = new Set<number>();
    perDay.forEach(d => d.forEach(p => minuteSet.add(p.t)));
    const minutes = Array.from(minuteSet).sort((a, b) => a - b);

    const avgPoints: number[][] = [];
    const medPoints: number[][] = [];
    const trimPoints: number[][] = [];

    minutes.forEach(min => {
      const values: number[] = [];
      perDay.forEach(d => {
        const v = this.interpolateAt(d, min);
        if (v != null) {
          values.push(v);
        }
      });
      // Need at least two days covering this time-of-day.
      if (values.length < 2) {
        return;
      }
      if (chart.showAverage) { avgPoints.push([min, this.mean(values)]); }
      if (chart.showMedian) { medPoints.push([min, this.median(values)]); }
      if (chart.showTrimmedMean) { trimPoints.push([min, this.trimmedMean(values, 0.1)]); }
    });

    const color = this.colorScheme[seriesIndex % this.colorScheme.length];
    const pushAgg = (label: string, points: number[][]) => {
      if (points.length === 0) {
        return;
      }
      series.push({
        name: seriesName + ' — ' + label,
        type: 'line',
        showSymbol: false,
        smooth: false,
        yAxisIndex: yAxisIndex,
        z: 5,                                   // draw above the per-day lines
        lineStyle: { color: color, width: 4 },  // bold so it stands out
        itemStyle: { color: color },
        data: points
      });
    };

    pushAgg('Average', avgPoints);
    pushAgg('Median', medPoints);
    pushAgg('Trimmed-Mean', trimPoints);
  }


  // Build a polar chart. Two shapes, chosen automatically:
  //  - Wind rose: for directional series (wind/direction, or 0-360 values), the
  //    angle is the reading value bucketed by polarBucketDegrees and the radius
  //    is the count of readings in each bucket.
  //  - Time-of-day polar: otherwise, the angle is the reading's time of day
  //    (0-24h) and the radius is the reading value.
  // Every non-aggregate series is overlaid on the shared polar coordinate system.
  private buildPolarOption(chart: ChartInfo, chartIndex: number): EChartsOption {
    const dataSeries = chart.chartData.filter(d => !d.isAggregate && d.series.length > 0);
    const isWindRose = dataSeries.some(d => this.isDirectionalSeries(d.name, d.series));

    const baseOption: any = {
      animation: false,
      backgroundColor: this.backgroundColor(),
      darkMode: this.darkBackground,
      tooltip: { trigger: 'item' },
      toolbox: this.toolbox(),
      legend: this.legend(chartIndex),
      polar: {},
      series: []
    };

    if (isWindRose) {
      const bucket = this.polarBucketDegrees > 0 ? this.polarBucketDegrees : 5;
      const bucketCount = Math.round(360 / bucket);

      // Angle categories at each bucket boundary (0, bucket, 2*bucket, ...).
      const categories: string[] = [];
      for (let i = 0; i < bucketCount; i++) {
        categories.push((i * bucket).toFixed(bucket % 1 === 0 ? 0 : 1) + '°');
      }

      baseOption.angleAxis = {
        type: 'category',
        data: categories,
        startAngle: 90,       // 0° (North) at the top
        clockwise: true,      // compass direction increases clockwise
        boundaryGap: false
      };
      baseOption.radiusAxis = { type: 'value', min: 0 };

      baseOption.series = dataSeries.map((data, index) => {
        const counts = new Array(bucketCount).fill(0);
        data.series.forEach(p => {
          let v = Number(p.value);
          if (isNaN(v)) {
            return;
          }
          v = ((v % 360) + 360) % 360;   // normalize to [0,360)
          const bin = Math.floor(v / bucket) % bucketCount;
          counts[bin]++;
        });

        const color = this.colorScheme[index % this.colorScheme.length];
        return {
          name: data.name,
          type: 'bar',
          coordinateSystem: 'polar',
          stack: undefined,
          itemStyle: { color: color, opacity: 0.7 },
          data: counts
        };
      });
    } else {
      // Time-of-day polar: angle = hour of day (0-24), radius = value.
      baseOption.angleAxis = {
        type: 'value',
        min: 0,
        max: 24,
        interval: 2,
        startAngle: 90,
        clockwise: true,
        axisLabel: {
          formatter: (value: number) => {
            const h = Math.floor(value) % 24;
            return (h < 10 ? '0' + h : '' + h) + ':00';
          }
        }
      };
      baseOption.radiusAxis = { type: 'value', scale: true };

      baseOption.series = dataSeries.map((data, index) => {
        const color = this.colorScheme[index % this.colorScheme.length];
        const points = data.series
          .map(p => {
            const d = p.name instanceof Date ? p.name : new Date(p.name);
            const value = Number(p.value);
            if (isNaN(d.getTime()) || isNaN(value)) {
              return null;
            }
            const hourOfDay = d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
            // ECharts polar line/scatter data: [radius, angle].
            return [value, hourOfDay];
          })
          .filter(p => p != null)
          // Order by angle so a connecting line follows the clock.
          .sort((a, b) => a[1] - b[1]);

        return {
          name: data.name,
          type: 'line',
          coordinateSystem: 'polar',
          showSymbol: false,
          lineStyle: { color: color },
          itemStyle: { color: color },
          data: points
        };
      });
    }

    return baseOption as EChartsOption;
  }


  // Return a shallow copy of the chart whose chartData is limited to the named
  // series (used to restrict a Time-x scatter to the selected Y series).
  private filterChartToSeries(chart: ChartInfo, seriesNames: string[]): ChartInfo {
    return {
      ...chart,
      chartData: chart.chartData.filter(d => seriesNames.includes(d.name))
    };
  }


  // Normalize a chart data series (by name) to sorted [{t, v}] pairs.
  private normalizedSeriesByName(chart: ChartInfo, name: string): { t: number; v: number }[] {
    const data = chart.chartData.find(d => d.name === name);
    if (data == undefined) {
      return [];
    }
    return data.series
      .map(p => ({
        t: (p.name instanceof Date ? p.name : new Date(p.name)).getTime(),
        v: Number(p.value)
      }))
      .filter(p => !isNaN(p.t) && !isNaN(p.v))
      .sort((a, b) => a.t - b.t);
  }


  // Build a value-vs-value correlation scatter: for each selected X series and
  // each selected Y series, pair their values at common timestamps (union of
  // timestamps, linearly interpolated, no extrapolation) and plot X vs Y.
  private buildScatterCorrelationOption(chart: ChartInfo, chartIndex: number): EChartsOption {
    const series: any[] = [];
    let colorIndex = 0;

    this.scatterXSeries
      .filter(x => x !== ChartReadingsEchartModalComponent.SCATTER_TIME)
      .forEach(xName => {
        const xSeries = this.normalizedSeriesByName(chart, xName);

        this.scatterYSeries.forEach(yName => {
          const ySeries = this.normalizedSeriesByName(chart, yName);
          if (xSeries.length === 0 || ySeries.length === 0) {
            return;
          }

          // Union of timestamps, ascending; keep points where both series cover.
          const timestampSet = new Set<number>();
          xSeries.forEach(p => timestampSet.add(p.t));
          ySeries.forEach(p => timestampSet.add(p.t));
          const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

          const points: number[][] = [];
          timestamps.forEach(t => {
            const xv = this.interpolateAt(xSeries, t);
            const yv = this.interpolateAt(ySeries, t);
            if (xv != null && yv != null) {
              points.push([xv, yv]);
            }
          });

          if (points.length > 0) {
            const color = this.colorScheme[colorIndex % this.colorScheme.length];
            series.push({
              name: yName + ' vs ' + xName,
              type: 'scatter',
              symbolSize: 6,
              itemStyle: { color: color },
              data: points
            });

            // Least-squares linear trendline (y = slope*x + intercept) with R².
            const fit = this.linearRegression(points);
            if (fit != null) {
              // Single pass rather than Math.min/max(...xs): spreading a large array
              // into a call overflows the argument/stack limit on big series
              // ("Maximum call stack size exceeded"). points is non-empty here.
              let xMin = points[0][0];
              let xMax = points[0][0];
              for (const p of points) {
                if (p[0] < xMin) xMin = p[0];
                if (p[0] > xMax) xMax = p[0];
              }
              series.push({
                name: yName + ' vs ' + xName + ' — R² ' + fit.r2.toFixed(3),
                type: 'line',
                showSymbol: false,
                lineStyle: { color: color, type: 'dashed', width: 2 },
                itemStyle: { color: color },
                tooltip: { show: false },
                data: [
                  [xMin, fit.slope * xMin + fit.intercept],
                  [xMax, fit.slope * xMax + fit.intercept]
                ]
              });
            }

            colorIndex++;
          }
        });
      });

    return {
      animation: false,
      backgroundColor: this.backgroundColor(),
      darkMode: this.darkBackground,
      tooltip: {
        trigger: 'item',
        axisPointer: { type: 'cross' }
      },
      toolbox: this.toolbox(),
      legend: this.legend(chartIndex),
      grid: {
        left: 60 + this.reservedYSliderWidth,
        right: 40 + this.reservedLegendWidth,
        top: 40,
        bottom: 70
      },
      // No axis names: the joined X/Y series lists are long and redundant with
      // the legend ("<Y> vs <X>" per series), and an x-axis name would overlap
      // the zoom slider below the plot.
      xAxis: {
        type: 'value',
        scale: true
      },
      yAxis: {
        type: 'value',
        scale: true
      },
      dataZoom: this.dataZoom(chartIndex),
      series: series as any
    };
  }


  // Background color for the chart based on the dark-background toggle.
  private backgroundColor(): string {
    return this.darkBackground ? this.darkBackgroundColor : this.lightBackgroundColor;
  }


  // Fixed min for the primary ('Value') axis: the user's manual scaling for that
  // axis, else the chart's own scale value, else undefined = autoscale.
  private primaryYMin(chart: ChartInfo): number | undefined {
    const min = this.axisManualMin('Value');
    if (min != undefined) {
      return min;
    }
    return chart.yMinScale ?? undefined;
  }


  // Fixed max for the primary ('Value') axis. See primaryYMin.
  private primaryYMax(chart: ChartInfo): number | undefined {
    const max = this.axisManualMax('Value');
    if (max != undefined) {
      return max;
    }
    return chart.yMaxScale ?? undefined;
  }


  // Manual-scaling min bound for a given axis key, or undefined (= autoscale that
  // end) when the axis has no manual scaling or the value is blank/non-numeric.
  private axisManualMin(key: string): number | undefined {
    const scale = this.manualScaleByAxis[key];
    return scale != undefined ? this.toNumberOrUndefined(scale.min) : undefined;
  }


  // Manual-scaling max bound for a given axis key. See axisManualMin.
  private axisManualMax(key: string): number | undefined {
    const scale = this.manualScaleByAxis[key];
    return scale != undefined ? this.toNumberOrUndefined(scale.max) : undefined;
  }


  // Whether the axis has any manual bound set (min and/or max).
  private axisHasManualScale(key: string): boolean {
    return this.axisManualMin(key) != undefined || this.axisManualMax(key) != undefined;
  }


  // The axis title to render: the base key, with a trailing ' *' when the axis
  // has manual scaling applied.
  private axisDisplayName(key: string): string {
    return this.axisHasManualScale(key) ? key + ' *' : key;
  }


  // Deep copy the per-axis manual-scaling map (so persisted/applied snapshots
  // don't share nested objects with the live state).
  private cloneScaleMap(map: { [key: string]: { min: number | null; max: number | null } }):
      { [key: string]: { min: number | null; max: number | null } } {
    const copy: { [key: string]: { min: number | null; max: number | null } } = {};
    if (map != undefined) {
      Object.keys(map).forEach(k => {
        const v = map[k];
        if (v != undefined) {
          copy[k] = { min: v.min ?? null, max: v.max ?? null };
        }
      });
    }
    return copy;
  }


  // Parse a possibly-string/empty/null scale bound to a finite number, or
  // undefined when it is blank or not a valid number.
  private toNumberOrUndefined(value: any): number | undefined {
    if (value == undefined || value.toString().trim() === '') {
      return undefined;
    }
    const num = Number(value);
    return isNaN(num) ? undefined : num;
  }


  // Legend config: vertical, docked to the right of the chart and top-aligned.
  // 'scroll' type bounds it to the available height (top..bottom) and shows
  // page-through controls when the items overflow vertically.
  // Legend is shown only when requested AND not in minimal mode.
  private get effectiveShowLegend(): boolean {
    return this.showLegend && !this.minimal;
  }


  private legend(chartIndex: number): any {
    const legend: any = {
      show: this.effectiveShowLegend,
      type: 'scroll',
      orient: 'vertical',
      right: 10,
      top: 40,
      bottom: 70,
      textStyle: {
        color: this.darkBackground ? '#ffffff' : '#000000'
      }
    };

    // Re-apply the saved show/hide selection so it survives option rebuilds.
    const selected = this.legendSelected[chartIndex];
    if (selected != undefined) {
      legend.selected = { ...selected };
    }

    return legend;
  }


  // Right-side grid space reserved for the vertical legend, including a gap
  // between the (right) y-axis and the legend (0 when hidden). Uses the fixed
  // legendWidth at build time; buildEchartOptions() replaces it with a width
  // measured from the actual legend labels (see adjustLegendWidth).
  private get reservedLegendWidth(): number {
    return this.effectiveShowLegend ? this.legendWidth + this.legendGap : 0;
  }


  // Min / max px the dynamic legend is allowed to occupy (label text only).
  private readonly legendMinWidth = 120;
  private readonly legendMaxWidth = 420;
  private _textMeasureCtx: CanvasRenderingContext2D;

  // Measure rendered text width (px) for the legend font, via a cached canvas.
  private measureTextWidth(text: string): number {
    if (this._textMeasureCtx == undefined) {
      const canvas = document.createElement('canvas');
      this._textMeasureCtx = canvas.getContext('2d');
      // Matches the ECharts default legend textStyle (fontSize 12, sans-serif).
      this._textMeasureCtx.font = '12px sans-serif';
    }
    return this._textMeasureCtx.measureText(text ?? '').width;
  }

  // Reserve right-margin space that fits the longest legend label, so long names
  // don't overflow the chart. Adjusts the already-built option's grid.right,
  // which was baked with the fixed reservedLegendWidth at build time.
  private adjustLegendWidth(option: any): void {
    if (!this.effectiveShowLegend || option == undefined || option.grid == undefined) {
      return;   // no legend, or a chart type without a cartesian grid (e.g. polar)
    }

    const names: string[] = (option.series ?? []).map((s: any) => s.name)
      .filter((n: any) => n != undefined && n !== ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME);
    if (names.length === 0) {
      return;
    }

    const longest = names.reduce((max, n) => Math.max(max, this.measureTextWidth(n)), 0);
    // Legend item = icon (~25px) + text + right padding; clamp to sane bounds.
    const measured = Math.min(this.legendMaxWidth, Math.max(this.legendMinWidth, Math.ceil(longest) + 25));

    const fixedReserve = this.legendWidth + this.legendGap;   // what the builder used
    const dynamicReserve = measured + this.legendGap;
    option.grid.right = (option.grid.right ?? this.legendGap) - fixedReserve + dynamicReserve;
  }


  // For a minimal embed: strip axis titles (name/nameGap) and tighten the grid
  // margins so the plot fills the small tile. No-op when not minimal.
  private applyMinimalLayout(option: any): void {
    if (!this.minimal || option == undefined) {
      return;
    }

    // Drop axis titles on any cartesian axes (xAxis / yAxis may be single or array).
    const clearAxisName = (axis: any) => {
      if (axis == undefined) {
        return;
      }
      if (Array.isArray(axis)) {
        axis.forEach(a => clearAxisName(a));
      } else {
        axis.name = '';
        axis.nameGap = 0;
      }
    };
    clearAxisName(option.xAxis);
    clearAxisName(option.yAxis);

    // Tighter grid padding now that there are no axis titles, legend, or zoom
    // bars taking space. containLabel keeps the tick labels inside these margins
    // (so multi-digit values aren't clipped) while still hugging the tile edges.
    if (option.grid != undefined) {
      option.grid = { left: 8, right: 12, top: 12, bottom: 8, containLabel: true };
    }
  }


  // Left-side grid space reserved for the vertical y-axis slider (0 when hidden).
  private get reservedYSliderWidth(): number {
    return this.showYAxisSlider ? this.yAxisSliderWidth : 0;
  }


  // Capture each chart's ECharts instance so the legend buttons can drive it.
  onChartInit(chartIndex: number, instance: any): void {
    this.echartInstances[chartIndex] = instance;

    // Labels are only drawn on the first chart.
    if (chartIndex === 0) {
      this.watchChartSize(instance);
    }
  }


  // Keep labelScale in step with the chart's width. ECharts itself is resized by ngx-echarts; this only
  // redraws the label marks when the scale changes. Watched outside Angular's zone with one animation
  // frame handled at a time, so dragging the modal's resize handle does not run change detection per frame.
  // A ResizeObserver reports once when it starts watching, which sets the right scale after the first draw.
  private watchChartSize(instance: any): void {
    this.chartSizeObserver?.disconnect();

    const container: HTMLElement | undefined = instance?.getDom?.();
    if (container == undefined || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.chartSizeObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect?.width ?? 0;
        if (width === 0) {
          return;   // hidden (e.g. a collapsed tab): nothing to lay out
        }
        cancelAnimationFrame(this.chartSizeFrame);
        this.chartSizeFrame = requestAnimationFrame(() => this.applyLabelScale(width));
      });
      this.chartSizeObserver.observe(container);
    });
  }


  // Rounded so a continuous resize redraws the labels every few pixels rather than every pixel.
  private applyLabelScale(widthPx: number): void {
    const scale = Math.round(labelFontScale(widthPx) * 100) / 100;
    if (scale === this.labelScale) {
      return;
    }
    this.labelScale = scale;

    const instance = this.echartInstances[0];
    if (instance == undefined || this.labels == undefined || this.labels.length === 0) {
      return;
    }

    // Only merge into the label series if the chart has it (labels may have loaded after the last build).
    const hasAnnotationSeries = ((instance.getOption?.()?.series ?? []) as any[])
      .some(s => s.id === ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME);
    if (!hasAnnotationSeries) {
      return;
    }

    this.refreshAnnotationSeries();
    if (this.labelsEditMode) {
      this.renderDragHandles();
    }
  }


  // ========================= Chart labels (annotations) =========================

  // Labels are only supported on the standard time chart (line/bar with a 'time'
  // x-axis), where a stored timestamp maps cleanly to the x-axis and a single
  // grid holds all series. Candlestick/polar/min-of-day/days-after-planting
  // are intentionally out of scope for v1.
  get annotationsApplicable(): boolean {
    return (this.echartType === 'line' || this.echartType === 'bar')
      && this.lineXMode === 'time';
  }


  // Fetch (or clear) the current favorite's labels, then rebuild so they render.
  private loadChartLabels(): void {
    const id = this.chartFavoriteId;
    if (id == undefined || id <= 0) {
      this.labels = [];
      this.lastFetchedFavoriteId = -1;
      this.buildEchartOptions();
      return;
    }
    if (id === this.lastFetchedFavoriteId) {
      return;
    }
    this.lastFetchedFavoriteId = id;
    this._sensorReadingsService.getChartLabels(id).subscribe(
      (result: ChartLabelInfo[]) => {
        this.labels = Array.isArray(result) ? result : [];
        this.pushLabelsToEditor();
        this.buildEchartOptions();
      },
      _err => {
        this.labels = [];
      });
  }


  // Build the invisible annotation series carrying every label's markers. Returns
  // null when labels don't apply (wrong mode / not chart 0 / no labels).
  private buildAnnotationSeries(chartIndex: number): any | null {
    if (chartIndex !== 0 || !this.annotationsApplicable || this.labels == undefined || this.labels.length === 0) {
      return null;
    }

    const markLineData: any[] = [];
    const markAreaData: any[] = [];
    const markPointData: any[] = [];

    this.labels.forEach(label => {
      const props = this.labelPropsOf(label);
      const type = label.labelType;

      // Bands are color-only (no text/arrow). The two bounds live in bandStart /
      // bandEnd: x epoch-ms for a vertical band (Type 3, spans full y-height),
      // y values for a horizontal band (Type 5, spans full x-width).
      if (type === 3 || type === 5) {
        const lo = this.toMs(label.bandStart);
        const hi = this.toMs(label.bandEnd);
        if (lo != undefined && hi != undefined) {
          const startBound: any = type === 3 ? { xAxis: lo } : { yAxis: lo };
          const endBound: any = type === 3 ? { xAxis: hi } : { yAxis: hi };
          startBound.itemStyle = { color: props.bandColor, opacity: 0.2 };
          markAreaData.push([startBound, endBound]);
        }
        return;
      }

      // Text labels (Types 1, 2, 4).
      const boxMs = this.toMs(label.boxTimestamp);
      const boxVal = label.boxValue;
      if (boxMs == undefined || boxVal == undefined) {
        return;
      }

      const targetMs = this.toMs(label.targetTimestamp);
      const targetVal = label.targetValue;
      const drawArrow = type !== 4 && props.showArrow !== false && targetMs != undefined && targetVal != undefined;

      if (drawArrow) {
        // Types 1-2: a box (text, at the start) + an arrow line to the target.
        markLineData.push([
          {
            coord: [boxMs, boxVal],
            symbol: 'none',
            lineStyle: { color: props.fontColor, width: 1, type: 'solid' },
            label: this.labelTextStyle(label, props, 'start')
          },
          { coord: [targetMs, targetVal], symbol: 'arrow', symbolSize: 10 }
        ]);
      } else {
        // Type 4 (or arrow disabled): just the box, drawn as an invisible point.
        markPointData.push({
          coord: [boxMs, boxVal],
          symbol: 'circle',
          symbolSize: 0,
          label: this.labelTextStyle(label, props, 'inside')
        });
      }
    });

    const series: any = {
      id: ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME,
      name: ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME,
      type: 'line',
      data: [],
      showSymbol: false,
      silent: true,
      legendHoverLink: false,
      tooltip: { show: false },
      z: 10,
      emphasis: { disabled: true },
      // Identity blur so labels don't fade when another series is hovered.
      blur: { lineStyle: { opacity: 1 }, areaStyle: { opacity: 1 }, itemStyle: { opacity: 1 } }
    };

    if (markLineData.length > 0) {
      series.markLine = {
        symbol: ['none', 'none'], silent: true, animation: false,
        label: { show: true }, lineStyle: { width: 1 }, emphasis: { disabled: true }, data: markLineData
      };
    }
    if (markAreaData.length > 0) {
      series.markArea = { silent: true, animation: false, emphasis: { disabled: true }, data: markAreaData };
    }
    if (markPointData.length > 0) {
      series.markPoint = {
        silent: true, animation: false, symbol: 'circle', symbolSize: 0,
        label: { show: true }, emphasis: { disabled: true }, data: markPointData
      };
    }

    return series;
  }


  // Parse a label's JSON styling bag, filling sensible per-type defaults.
  private labelPropsOf(label: ChartLabelInfo): ChartLabelProperties {
    let p: ChartLabelProperties = {};
    if (label.properties != undefined && label.properties !== '') {
      try {
        p = JSON.parse(label.properties);
      } catch {
        p = {};
      }
    }
    return {
      fontSize: p.fontSize ?? 12,
      fontColor: p.fontColor ?? (this.darkBackground ? '#eeeeee' : '#222222'),
      hasStickyBackground: p.hasStickyBackground ?? (label.labelType === 2),
      hasShadow: p.hasShadow ?? (label.labelType === 2 || label.labelType === 4),
      bandColor: p.bandColor ?? '#91cc75',
      showArrow: p.showArrow ?? (label.labelType !== 4)
    };
  }


  // Null-safe epoch-ms accessor (undefined when missing/invalid).
  private toMs(value: number | null): number | undefined {
    if (value == undefined || isNaN(value)) {
      return undefined;
    }
    return value;
  }


  // Build the ECharts label style (text, font, optional sticky note + shadow). The font and the padding
  // scale with the chart's width (labelScale), like image favorite labels; border, radius and shadow don't.
  private labelTextStyle(label: ChartLabelInfo, props: ChartLabelProperties, position: string): any {
    const sticky = props.hasStickyBackground === true;
    const shadow = props.hasShadow === true;
    const text = label.text ?? '';
    const scale = this.labelScale;
    return {
      show: true,
      position: position,
      formatter: () => text,
      color: props.fontColor,
      fontSize: props.fontSize * scale,
      backgroundColor: sticky ? '#FFF9C4' : 'transparent',
      borderColor: sticky ? '#E6D879' : 'transparent',
      borderWidth: sticky ? 1 : 0,
      borderRadius: 4,
      padding: (sticky ? [6, 8] : [2, 4]).map(p => p * scale),
      shadowBlur: shadow ? 6 : 0,
      shadowColor: 'rgba(0,0,0,0.35)',
      shadowOffsetX: shadow ? 2 : 0,
      shadowOffsetY: shadow ? 2 : 0
    };
  }


  // ----- Label editing (button -> editor modal + on-chart drag handles) -----

  toggleLabelEditing(): void {
    if (this.labelsEditMode) {
      this.exitLabelEditing();
    } else {
      this.enterLabelEditing();
    }
  }


  private enterLabelEditing(): void {
    if (!this.annotationsApplicable || !this.labelsEditable) {
      return;
    }
    this.labelsEditMode = true;
    this.openLabelEditor();
    // Defer so the chart has rendered before projecting handle pixel positions.
    setTimeout(() => this.renderDragHandles(), 0);
  }


  private exitLabelEditing(): void {
    this.labelsEditMode = false;
    this.clearDragHandles();
    if (this.labelEditorRef != undefined) {
      this.labelEditorRef.hide();
      this.labelEditorRef = null;
    }
  }


  private openLabelEditor(): void {
    this.labelEditorRef = this.modalService.show(ChartLabelEditorModalComponent, {
      animated: false,
      backdrop: false,
      ignoreBackdropClick: true,
      class: 'chart-label-editor-dialog',
      initialState: { labels: this.labels }
    });
    const content = this.labelEditorRef.content as ChartLabelEditorModalComponent;
    content.onAdd.subscribe((type: number) => this.addLabel(type));
    content.onUpdate.subscribe((label: ChartLabelInfo) => this.persistLabel(label, true));
    content.onDelete.subscribe((id: number) => this.deleteLabel(id));
    content.onClosed.subscribe(() => this.exitLabelEditing());
  }


  private pushLabelsToEditor(selectId?: number): void {
    if (this.labelEditorRef == undefined || this.labelEditorRef.content == undefined) {
      return;
    }
    const content = this.labelEditorRef.content as ChartLabelEditorModalComponent;
    content.labels = this.labels;
    if (selectId != undefined) {
      content.selectById(selectId);
    }
  }


  // Create a new label of the given type, placed near the center of the current
  // view (with the box offset from the target so the arrow is visible).
  private addLabel(type: number): void {
    const instance = this.echartInstances[0];
    if (instance == undefined || this.chartFavoriteId <= 0) {
      return;
    }

    const w = instance.getWidth();
    const h = instance.getHeight();

    const props: ChartLabelProperties = {
      fontSize: 12,
      fontColor: this.darkBackground ? '#eeeeee' : '#222222',
      hasStickyBackground: type === 2,
      hasShadow: type === 2 || type === 4,
      bandColor: '#91cc75',
      showArrow: type === 1 || type === 2
    };

    const request: ChartLabelRequest = {
      id: 0,
      chartFavoriteID: this.chartFavoriteId,
      labelType: type,
      text: '',
      boxTimestamp: null,
      boxValue: null,
      targetTimestamp: null,
      targetValue: null,
      bandStart: null,
      bandEnd: null,
      properties: JSON.stringify(props)
    };

    if (type === 3) {
      // Vertical band: default x-range around the view center.
      const bs = this.pixelToData(instance, w / 2 - 60, h / 2);
      const be = this.pixelToData(instance, w / 2 + 60, h / 2);
      if (bs == undefined || be == undefined) {
        return;
      }
      request.bandStart = bs[0];
      request.bandEnd = be[0];
    } else if (type === 5) {
      // Horizontal band: default y-range around the view center (a lower pixel y
      // is a larger value, so the top handle is the upper bound).
      const lo = this.pixelToData(instance, w / 2, h / 2 + 40);
      const hi = this.pixelToData(instance, w / 2, h / 2 - 40);
      if (lo == undefined || hi == undefined) {
        return;
      }
      request.bandStart = lo[1];
      request.bandEnd = hi[1];
    } else {
      // Text label: a box offset up-left from the target so the arrow shows (the offset grows with the
      // label font so the bigger box does not start on top of its own arrow).
      const target = this.pixelToData(instance, w / 2, h / 2);
      const box = this.pixelToData(instance, w / 2 - 80 * this.labelScale, h / 2 - 60 * this.labelScale);
      if (target == undefined || box == undefined) {
        return;
      }
      request.text = 'New label';
      request.boxTimestamp = box[0];
      request.boxValue = box[1];
      request.targetTimestamp = type === 4 ? null : target[0];
      request.targetValue = type === 4 ? null : target[1];
    }

    this._sensorReadingsService.createChartLabel(request).subscribe(
      (newId: any) => {
        const id = Number(newId);
        if (isNaN(id) || id <= 0) {
          this.toastr.error('Unable to add label');
          return;
        }
        const info: ChartLabelInfo = { ...request, id } as ChartLabelInfo;
        this.labels = [...this.labels, info];
        this.pushLabelsToEditor(id);
        this.buildEchartOptions();
        setTimeout(() => this.renderDragHandles(), 0);
      },
      _err => {
        this.toastr.error('Unable to add label');
      });
  }


  // Persist a label's current state. When rebuild is true (property/type edits
  // from the modal) the whole option is rebuilt and handles re-projected; drag
  // commits pass false (they refresh the marks + handles themselves).
  private persistLabel(label: ChartLabelInfo, rebuild: boolean): void {
    if (label == undefined || label.id <= 0) {
      return;
    }
    // A property/type edit may have introduced an arrow or band that has no
    // geometry yet — seed it so its drag handles appear.
    if (rebuild) {
      this.ensureGeometryForType(label);
    }

    this._sensorReadingsService.updateChartLabel(this.toLabelRequest(label), this.username).subscribe(
      () => { },
      _err => this.toastr.error('Unable to save label'));

    if (rebuild) {
      this.buildEchartOptions();
      setTimeout(() => this.renderDragHandles(), 0);
    }
  }


  // Seed any geometry a label's current type needs but is missing — e.g. after
  // the user switches a label's type in the editor — so its drag handles appear.
  private ensureGeometryForType(label: ChartLabelInfo): void {
    const instance = this.echartInstances[0];
    if (instance == undefined) {
      return;
    }
    const w = instance.getWidth();
    const h = instance.getHeight();
    const center = this.pixelToData(instance, w / 2, h / 2);
    const type = label.labelType;

    // Vertical band: x-range around the view center.
    if (type === 3) {
      if (label.bandStart == undefined || label.bandEnd == undefined) {
        const bs = this.pixelToData(instance, w / 2 - 60, h / 2);
        const be = this.pixelToData(instance, w / 2 + 60, h / 2);
        if (bs != undefined) { label.bandStart = bs[0]; }
        if (be != undefined) { label.bandEnd = be[0]; }
      }
      return;
    }

    // Horizontal band: y-range around the view center.
    if (type === 5) {
      if (label.bandStart == undefined || label.bandEnd == undefined) {
        const lo = this.pixelToData(instance, w / 2, h / 2 + 40);
        const hi = this.pixelToData(instance, w / 2, h / 2 - 40);
        if (lo != undefined) { label.bandStart = lo[1]; }
        if (hi != undefined) { label.bandEnd = hi[1]; }
      }
      return;
    }

    // Text label: ensure a box, and (Types 1-2) an arrow target offset from it.
    if ((label.boxTimestamp == undefined || label.boxValue == undefined) && center != undefined) {
      label.boxTimestamp = center[0];
      label.boxValue = center[1];
    }
    if (type !== 4 && (label.targetTimestamp == undefined || label.targetValue == undefined)) {
      const boxPx = (label.boxTimestamp != undefined && label.boxValue != undefined)
        ? this.dataToPixel(instance, label.boxTimestamp, label.boxValue) : undefined;
      const base = boxPx ?? [w / 2, h / 2];
      const t = this.pixelToData(instance, base[0] + 70 * this.labelScale, base[1] + 50 * this.labelScale);
      if (t != undefined) {
        label.targetTimestamp = t[0];
        label.targetValue = t[1];
      }
    }
  }


  private deleteLabel(id: number): void {
    if (id <= 0) {
      return;
    }
    this._sensorReadingsService.deleteChartLabel(id, this.username).subscribe(
      () => { },
      _err => this.toastr.error('Unable to delete label'));

    this.labels = this.labels.filter(l => l.id !== id);
    this.pushLabelsToEditor();
    this.buildEchartOptions();
    setTimeout(() => this.renderDragHandles(), 0);
  }


  private toLabelRequest(label: ChartLabelInfo): ChartLabelRequest {
    return {
      id: label.id,
      chartFavoriteID: this.chartFavoriteId,
      labelType: label.labelType,
      text: label.text,
      boxTimestamp: label.boxTimestamp,
      boxValue: label.boxValue,
      targetTimestamp: label.targetTimestamp,
      targetValue: label.targetValue,
      bandStart: label.bandStart,
      bandEnd: label.bandEnd,
      properties: label.properties
    };
  }


  // Draw a draggable handle per editable point of each label (box, arrow target,
  // band edges), projected to pixels from the labels' data coords.
  private renderDragHandles(): void {
    const instance = this.echartInstances[0];
    if (instance == undefined || !this.labelsEditMode) {
      return;
    }
    if (!this.annotationsApplicable) {
      this.clearDragHandles();
      return;
    }

    const w = instance.getWidth();
    const h = instance.getHeight();
    const center = this.pixelToData(instance, w / 2, h / 2);

    const graphics: any[] = [];

    this.labels.forEach((label, li) => {
      const type = label.labelType;

      // Bands get two edge handles. A vertical band moves on x (handles sit at
      // mid-height); a horizontal band moves on y (handles sit at mid-width).
      if (type === 3 || type === 5) {
        const lo = this.toMs(label.bandStart);
        const hi = this.toMs(label.bandEnd);
        const place = (bound: number) => type === 3
          ? this.dataToPixel(instance, bound, center != undefined ? center[1] : 0)
          : this.dataToPixel(instance, center != undefined ? center[0] : bound, bound);
        if (lo != undefined) {
          const px = place(lo);
          if (px != undefined) {
            graphics.push(this.makeHandle('lblbs_' + li, px, '#43a047', li, 'bandStart'));
          }
        }
        if (hi != undefined) {
          const px = place(hi);
          if (px != undefined) {
            graphics.push(this.makeHandle('lblbe_' + li, px, '#43a047', li, 'bandEnd'));
          }
        }
        return;
      }

      // Text labels: a box handle + (Types 1-2) an arrow-target handle.
      const boxMs = this.toMs(label.boxTimestamp);
      if (boxMs != undefined && label.boxValue != undefined) {
        const px = this.dataToPixel(instance, boxMs, label.boxValue);
        if (px != undefined) {
          graphics.push(this.makeHandle('lblbox_' + li, px, '#1e88e5', li, 'box'));
        }
      }

      if (type !== 4) {
        const tMs = this.toMs(label.targetTimestamp);
        if (tMs != undefined && label.targetValue != undefined) {
          const px = this.dataToPixel(instance, tMs, label.targetValue);
          if (px != undefined) {
            graphics.push(this.makeHandle('lbltgt_' + li, px, '#e53935', li, 'target'));
          }
        }
      }
    });

    instance.setOption({ graphic: graphics }, { replaceMerge: ['graphic'] });
  }


  // Read a dragged graphic element's current pixel position, tolerating either
  // the ZRender 5 x/y transform or the legacy position array.
  private handlePosition(el: any): [number, number] {
    if (el != undefined && typeof el.x === 'number' && typeof el.y === 'number') {
      return [el.x, el.y];
    }
    if (el != undefined && Array.isArray(el.position) && el.position.length === 2) {
      return [el.position[0], el.position[1]];
    }
    return [0, 0];
  }


  private clearDragHandles(): void {
    const instance = this.echartInstances[0];
    if (instance == undefined) {
      return;
    }
    instance.setOption({ graphic: [] }, { replaceMerge: ['graphic'] });
  }


  private makeHandle(id: string, px: [number, number], color: string, labelIndex: number, kind: string): any {
    const self = this;
    return {
      id: id,
      type: 'circle',
      position: [px[0], px[1]],
      shape: { cx: 0, cy: 0, r: 7 },
      style: { fill: color, stroke: '#ffffff', lineWidth: 2, shadowBlur: 2, shadowColor: 'rgba(0,0,0,0.4)' },
      z: 100,
      draggable: true,
      ondrag: function (this: any) {
        // `this` is the zrender element being dragged.
        self.onHandleDrag(labelIndex, kind, self.handlePosition(this));
      },
      ondragend: function (this: any) {
        self.onHandleDragEnd(labelIndex, kind, self.handlePosition(this));
      }
    };
  }


  // Live update while dragging: convert the handle's pixel position back to data
  // coords, update the label, and refresh just the annotation marks (not a full
  // rebuild, so other handles aren't disturbed mid-drag).
  private onHandleDrag(labelIndex: number, kind: string, px: [number, number]): void {
    const instance = this.echartInstances[0];
    if (instance == undefined) {
      return;
    }
    const data = this.pixelToData(instance, px[0], px[1]);
    if (data == undefined) {
      return;
    }
    const label = this.labels[labelIndex];
    if (label == undefined) {
      return;
    }
    this.applyHandleToLabel(label, kind, data);
    this.refreshAnnotationSeries();
  }


  private onHandleDragEnd(labelIndex: number, kind: string, px: [number, number]): void {
    this.onHandleDrag(labelIndex, kind, px);
    const label = this.labels[labelIndex];
    if (label != undefined) {
      this.persistLabel(label, false);
    }
    // Re-project all handles to the committed data coords.
    this.renderDragHandles();
  }


  private applyHandleToLabel(label: ChartLabelInfo, kind: string, data: [number, number]): void {
    // A band's bound is x (epoch ms) for a vertical band, y (value) for a
    // horizontal band; text anchors always use x = data[0], y = data[1].
    const bandBound = label.labelType === 5 ? data[1] : data[0];
    if (kind === 'box') {
      label.boxTimestamp = data[0];
      label.boxValue = data[1];
    } else if (kind === 'target') {
      label.targetTimestamp = data[0];
      label.targetValue = data[1];
    } else if (kind === 'bandStart') {
      label.bandStart = bandBound;
    } else if (kind === 'bandEnd') {
      label.bandEnd = bandBound;
    }
  }


  // Merge the current label markers into the existing annotation series (by id),
  // without rebuilding the whole option (keeps zoom + the other drag handles).
  private refreshAnnotationSeries(): void {
    const instance = this.echartInstances[0];
    if (instance == undefined) {
      return;
    }
    const series = this.buildAnnotationSeries(0);
    if (series == undefined) {
      return;
    }
    instance.setOption({
      series: [{
        id: ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME,
        markLine: series.markLine ?? { data: [] },
        markArea: series.markArea ?? { data: [] },
        markPoint: series.markPoint ?? { data: [] }
      }]
    });
  }


  private pixelToData(instance: any, px: number, py: number): [number, number] | undefined {
    try {
      const d = instance.convertFromPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [px, py]);
      if (Array.isArray(d) && d.length === 2 && !isNaN(d[0]) && !isNaN(d[1])) {
        return [d[0], d[1]];
      }
    } catch {
      // Chart not laid out yet / point outside the grid.
    }
    return undefined;
  }


  private dataToPixel(instance: any, ms: number, val: number): [number, number] | undefined {
    try {
      const p = instance.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [ms, val]);
      if (Array.isArray(p) && p.length === 2 && !isNaN(p[0]) && !isNaN(p[1])) {
        return [p[0], p[1]];
      }
    } catch {
      // Chart not laid out yet.
    }
    return undefined;
  }


  // Handle clicks on a Y-axis: clicking its title moves that axis to the left;
  // clicking its values opens the manual-scaling modal for that axis. The axis is
  // resolved from the click's componentIndex via the base keys captured at build
  // time (the rendered name can carry a ' *' suffix, so we don't parse it).
  onChartClick(chartIndex: number, params: any): void {
    if (params == undefined || params.componentType !== 'yAxis') {
      return;
    }

    const keys = this.axisKeysByChart[chartIndex];
    const key = keys != undefined ? keys[params.componentIndex] : undefined;
    if (key == undefined || key === '') {
      return;
    }

    if (params.targetType === 'axisName') {
      // Move this axis to the left (no-op if it is already the left axis).
      if (key !== this.leftAxisReadingType) {
        this.leftAxisReadingType = key;
        this.buildEchartOptions();
        this.emitChartDetailsChanged();
      }
    } else if (params.targetType === 'axisLabel') {
      // Set manual scaling (min/max) for this axis.
      this.openManualScaleModal(key);
    }
  }


  // Right-click on a data series opens a small menu to choose its chart type.
  // Annotation and running-sum series are not overridable.
  onChartContextMenu(chartIndex: number, params: any): void {
    if (!this.seriesTypeOverridesApplicable || params == undefined || params.componentType !== 'series') {
      return;
    }

    const name: string = params.seriesName;
    if (name == undefined || name === ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME || name.endsWith(' (sum)')) {
      return;
    }

    // Suppress the browser's own context menu.
    params.event?.event?.preventDefault?.();

    this.seriesMenu = {
      name,
      x: params.event?.offsetX ?? 0,
      y: params.event?.offsetY ?? 0
    };
  }


  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  closeSeriesMenu(): void {
    this.seriesMenu = null;
  }


  // Set (or, with null, clear) one series' chart type override.
  setSeriesType(name: string, type: SeriesChartType | null): void {
    if (type == null) {
      delete this.seriesTypeOverrides[name];
    } else {
      this.seriesTypeOverrides[name] = type;
    }
    this.seriesMenu = null;
    this.buildEchartOptions();
    this.emitChartDetailsChanged();
  }


  // Open the per-series chart type dialog, listing every overridable series.
  openSeriesTypesModal(): void {
    const instanceNames = this.renderedSeriesNames(0).filter(n => !n.endsWith(' (sum)'));
    // Colors follow the series order, like the chart itself.
    const rows = instanceNames.map((name, i) => ({
      name,
      color: this.colorScheme[i % this.colorScheme.length],
      type: (this.seriesTypeOverrides[name] ?? '') as SeriesChartType | ''
    }));

    const params: SeriesTypeModalParams = {
      rows,
      defaultTypeLabel: this.seriesChartTypes.find(t => t.value === this.defaultSeriesType)?.label ?? 'Line'
    };

    const modalRef = this.modalService.show(SeriesTypeModalComponent, {
      animated: false,
      backdrop: false,
      ignoreBackdropClick: true,
      initialState: params
    });

    modalRef.content.onApply.subscribe((result: SeriesTypeResult) => {
      // Series not listed in the dialog (hidden by a rebuild) keep their override.
      const listed = new Set(instanceNames);
      const kept: { [name: string]: SeriesChartType } = {};
      Object.keys(this.seriesTypeOverrides).forEach(n => {
        if (!listed.has(n)) { kept[n] = this.seriesTypeOverrides[n]; }
      });
      this.seriesTypeOverrides = { ...kept, ...result };
      this.buildEchartOptions();
      this.emitChartDetailsChanged();
    });
  }


  // Open the min/max modal for an axis, seeded with its current scaling, and
  // apply the result. Blank bounds autoscale that end; clearing both removes the
  // axis's manual scaling (and its '*'). Persisted via the ChartDetails round-trip.
  private openManualScaleModal(key: string): void {
    const current = this.manualScaleByAxis[key];
    const params: MinMaxScaleModalParams = {
      title: 'Set Y-Axis Range: ' + key,
      min: current != undefined ? current.min : null,
      max: current != undefined ? current.max : null
    };

    const modalRef = this.modalService.show(MinMaxScaleModalComponent, {
      animated: false,
      backdrop: false,
      ignoreBackdropClick: true,
      initialState: params
    });

    modalRef.content.onApply.subscribe((result: MinMaxScaleResult) => {
      const min = this.toNumberOrUndefined(result.min);
      const max = this.toNumberOrUndefined(result.max);
      if (min == undefined && max == undefined) {
        delete this.manualScaleByAxis[key];
      } else {
        this.manualScaleByAxis[key] = { min: min ?? null, max: max ?? null };
      }
      this.buildEchartOptions();
      this.emitChartDetailsChanged();
    });
  }


  // Remember the legend show/hide selection whenever the user toggles a series,
  // so it survives option rebuilds (chart type, aggregation, outliers, etc.).
  onLegendSelectChanged(chartIndex: number, event: any): void {
    if (event == undefined || event.selected == undefined) {
      return;
    }
    this.legendSelected[chartIndex] = { ...event.selected };
    this.emitChartDetailsChanged();
  }


  // Show every series in a chart's legend.
  legendSelectAll(chartIndex: number): void {
    const instance = this.echartInstances[chartIndex];
    if (instance == undefined) {
      return;
    }
    instance.dispatchAction({ type: 'legendAllSelect' });

    const names = this.renderedSeriesNames(chartIndex);
    const selected: { [name: string]: boolean } = {};
    names.forEach(name => selected[name] = true);
    this.legendSelected[chartIndex] = selected;
    this.emitChartDetailsChanged();
  }


  // Hide every series in a chart's legend.
  legendUnselectAll(chartIndex: number): void {
    const instance = this.echartInstances[chartIndex];
    if (instance == undefined) {
      return;
    }
    // There is no "unselect all" action, so unselect each series by name. Names
    // must come from the actual rendered series (which differ from chartData in
    // MinOfDay / polar / aggregate modes), not the raw chartData names.
    const names = this.renderedSeriesNames(chartIndex);
    const selected: { [name: string]: boolean } = {};
    names.forEach(name => {
      instance.dispatchAction({ type: 'legendUnSelect', name });
      selected[name] = false;
    });
    this.legendSelected[chartIndex] = selected;
    this.emitChartDetailsChanged();
  }


  // Names of the series actually rendered in a chart's ECharts option (these are
  // what the legend keys on).
  private renderedSeriesNames(chartIndex: number): string[] {
    const option: any = this.chartOptions[chartIndex];
    const seriesList: any[] = option?.series ?? [];
    return seriesList.map(s => s.name)
      .filter(n => n != undefined && n !== ChartReadingsEchartModalComponent.ANNOTATION_SERIES_NAME);
  }


  // Extract the reading type from a data-series name. The name is built as
  // "<type>, <legend> (c|o)", so the reading type is the text before the comma.
  private readingTypeOfSeries(seriesName: string): string {
    const commaIndex = seriesName.indexOf(',');
    return commaIndex >= 0 ? seriesName.substring(0, commaIndex).trim() : seriesName.trim();
  }


  // Build a candlestick option where each candle shows the high, low and
  // average of the readings falling within the selected time bucket.
  private buildCandlestickOption(chart: ChartInfo, chartIndex: number): EChartsOption {
    // Collect the union of bucket keys across all data series so every series
    // shares the same, ordered category x-axis.
    const bucketKeys = new Set<number>();
    const perSeries = chart.chartData.map(data => {
      const buckets = this.aggregateToBuckets(data.series);
      buckets.forEach((_, key) => bucketKeys.add(key));
      return { name: data.name, buckets };
    });

    const orderedKeys = Array.from(bucketKeys).sort((a, b) => a - b);
    const categories = orderedKeys.map(key => this.formatBucketLabel(key));

    const series = perSeries.map((s, index) => ({
      name: s.name,
      type: 'candlestick',
      itemStyle: {
        color: this.colorScheme[index % this.colorScheme.length],
        color0: this.colorScheme[index % this.colorScheme.length],
        borderColor: this.colorScheme[index % this.colorScheme.length],
        borderColor0: this.colorScheme[index % this.colorScheme.length]
      },
      // ECharts candlestick data order is [open, close, low, high]. We use the
      // bucket average for both open and close so the body marks the average,
      // and the wicks span low -> high.
      data: orderedKeys.map(key => {
        const b = s.buckets.get(key);
        if (b == null) {
          return ['-', '-', '-', '-'];
        }
        const avg = b.sum / b.count;
        return [avg, avg, b.min, b.max];
      })
    }));

    this.axisKeysByChart[chartIndex] = [ 'Value' ];

    return {
      animation: false,
      backgroundColor: this.backgroundColor(),
      darkMode: this.darkBackground,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      toolbox: this.toolbox(),
      legend: this.legend(chartIndex),
      grid: {
        left: 60 + this.reservedYSliderWidth,
        right: 40 + this.reservedLegendWidth,
        top: 40,
        bottom: 70
      },
      xAxis: {
        type: 'category',
        data: categories,
        name: 'Date-Time',
        nameLocation: 'middle',
        nameGap: 30,
        boundaryGap: true
      },
      yAxis: {
        type: 'value',
        name: this.axisDisplayName('Value'),
        scale: true,
        min: this.primaryYMin(chart),
        max: this.primaryYMax(chart),
        triggerEvent: true
      },
      dataZoom: this.dataZoom(chartIndex),
      series: series as any
    };
  }


  // Group a series of {name(Date), value} points into time buckets keyed by the
  // start-of-period timestamp, tracking min/max/sum/count for each bucket.
  private aggregateToBuckets(points: { name: any; value: any }[]): Map<number, { min: number; max: number; sum: number; count: number }> {
    const buckets = new Map<number, { min: number; max: number; sum: number; count: number }>();

    points.forEach(point => {
      const value = Number(point.value);
      if (isNaN(value)) {
        return;
      }

      const date = point.name instanceof Date ? point.name : new Date(point.name);
      const key = moment(date).startOf(this.candlestickBucket).valueOf();

      const existing = buckets.get(key);
      if (existing == null) {
        buckets.set(key, { min: value, max: value, sum: value, count: 1 });
      } else {
        existing.min = Math.min(existing.min, value);
        existing.max = Math.max(existing.max, value);
        existing.sum += value;
        existing.count += 1;
      }
    });

    return buckets;
  }


  // Format a bucket's start timestamp for the candlestick category axis.
  private formatBucketLabel(key: number): string {
    const m = moment(key);
    switch (this.candlestickBucket) {
      case 'hour':
        return m.format('YYYY-MM-DD HH:00');
      case 'week':
        return m.format('YYYY-MM-DD') + ' (wk)';
      default:
        return m.format('YYYY-MM-DD');
    }
  }


  // Shared toolbox config with the native JPG export. Omitted in minimal mode.
  private toolbox(): any {
    if (this.minimal) {
      return undefined;
    }
    return {
      right: 20,
      top: -10,
      feature: {
        saveAsImage: {
          type: 'jpeg',
          name: 'chart',
          backgroundColor: this.backgroundColor(),
          title: 'Save as JPG'
        }
      }
    };
  }


  // Rebuild the charts when the user changes the selected ECharts type.
  onEchartTypeChange(): void {
    // Rebuild data (not just re-render) so Daily Series are added/removed to
    // match the new type's applicability (candlestick/polar/scatter don't
    // support them; a stale series would otherwise render mangled).
    this.updateChartData();
    this.emitChartDetailsChanged();
  }


  // Rebuild the charts when the user changes the candlestick aggregation period.
  onCandlestickBucketChange(): void {
    this.buildEchartOptions();
    this.emitChartDetailsChanged();
  }


  // Rebuild the charts when the user toggles the independent Y-axis overlay.
  onOverlayIndependentYAxisChange(): void {
    this.buildEchartOptions();
    this.emitChartDetailsChanged();
  }


  // Handler for the user-driven per-chart controls (aggregate-series toggles and
  // ignore-outlier bounds): rebuild the data and notify the host so the change is
  // persisted. Kept separate from updateChartData() so internal rebuilds don't
  // emit.
  onPerChartOptionChange(): void {
    this.updateChartData();
    this.emitChartDetailsChanged();
  }


  // Whether a collapsible control group is currently expanded for a given chart.
  isGroupExpanded(group: ControlGroup, index: number): boolean {
    const expanded = this.expandedGroups[index];
    return expanded != undefined && expanded.has(group);
  }


  // Expand / collapse one control group. Pinned from here on so the choice
  // survives the next chart rebuild. Purely visual, so no chart rebuild and no
  // chartDetailsChanged emit (that would dirty the host's saved snapshot).
  toggleGroup(group: ControlGroup, index: number): void {
    if (this.expandedGroups[index] == undefined) {
      this.expandedGroups[index] = new Set<ControlGroup>();
    }
    if (this.pinnedGroups[index] == undefined) {
      this.pinnedGroups[index] = new Set<ControlGroup>();
    }

    if (this.expandedGroups[index].has(group)) {
      this.expandedGroups[index].delete(group);
    }
    else {
      this.expandedGroups[index].add(group);
    }

    this.pinnedGroups[index].add(group);
  }


  // Decide which control groups start expanded: those that already hold active
  // values (so a restored favorite shows its settings), except groups the user
  // has explicitly toggled, which keep whatever state they chose.
  private seedExpandedGroups(): void {
    const colorsCustomized = this.isColorSchemeCustomized();

    this.chartInfos.forEach((chart, i) => {
      const pinned = this.pinnedGroups[i] != undefined ? this.pinnedGroups[i] : new Set<ControlGroup>();
      const expanded = this.expandedGroups[i] != undefined ? this.expandedGroups[i] : new Set<ControlGroup>();

      const active: { group: ControlGroup; hasValues: boolean }[] = [
        { group: 'outliers', hasValues: this.hasOutlierValues(chart) },
        { group: 'aggregates', hasValues: this.hasAggregateValues(chart) },
        { group: 'daily', hasValues: this.hasDailyValues(chart) },
        { group: 'colors', hasValues: colorsCustomized }
      ];

      active.forEach(x => {
        if (pinned.has(x.group)) {
          return;
        }
        if (x.hasValues) {
          expanded.add(x.group);
        }
        else {
          expanded.delete(x.group);
        }
      });

      this.expandedGroups[i] = expanded;
      this.pinnedGroups[i] = pinned;
    });

    // Drop state for charts that no longer exist (e.g. fewer reading types
    // selected), so it is not reused if the chart count grows again.
    this.expandedGroups.length = this.chartInfos.length;
    this.pinnedGroups.length = this.chartInfos.length;
  }


  // Whether either ignore-outlier bound is set on a chart.
  private hasOutlierValues(chart: ChartInfo): boolean {
    return (chart.yMinIgnore != undefined && chart.yMinIgnore.toString() !== '')
        || (chart.yMaxIgnore != undefined && chart.yMaxIgnore.toString() !== '');
  }


  // Whether any cross-sensor aggregate series is turned on for a chart.
  private hasAggregateValues(chart: ChartInfo): boolean {
    return chart.showAverage === true || chart.showMedian === true
        || chart.showTrimmedMean === true || chart.showSum === true;
  }


  private hasDailyValues(chart: ChartInfo): boolean {
    return chart.showDailyAverage === true || chart.showDailyMedian === true
        || chart.showDailyTrimmedMean === true || chart.showDailyMin === true
        || chart.showDailyMax === true || chart.showDailySum === true;
  }


  // Whether the active palette differs from the default 5 colors.
  private isColorSchemeCustomized(): boolean {
    const defaults = ChartReadingsEchartModalComponent.DEFAULT_COLOR_SCHEME;
    if (this.colorScheme == undefined || this.colorScheme.length !== defaults.length) {
      return true;
    }
    return this.colorScheme.some((color, i) => color !== defaults[i]);
  }


}
